// screens-editor.tsx - the studio: Edit (default) / Map Builder / Animation Studio + Preview
// Plan is consolidated: stats live in the project Overview, floors live in Map Builder.
// Exports: ScreenEditor
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore, type CSSProperties, type MutableRefObject, type ReactNode } from 'react';
import { toast } from '../design/toast';

import {
  AnimationGalleryStage,
  AudioEngine,
  AutoRigPanel,
  BoneMappingPanel,
  buildLiveMapDocument,
  CharacterStudio,
  ClientLogicRuntime,
  CLIP_GROUPS,
  CORE_RIG_BONES,
  createNextFloor,
  deriveRoomGeometry,
  floorExtent,
  floorWorldY,
  GameRuntime,
  getObjectDefinition,
  getRigBones,
  hitTestStructure,
  getObjectRegistryVersion,
  isRoomDerived,
  isStoreDirty,
  isPrototypeClip,
  LibraryGalleryStage,
  listLibraryAnimatedModels,
  loadUal2MannequinFPrototypeGltf,
  loadUal2StandardPrototypeGltf,
  MapStructureViewport,
  NEUTRAL_BIPED_DEFINITION,
  NEUTRAL_BIPED_RIG_ID,
  panelFromDrag,
  removeFloor,
  RetargetedGalleryStage,
  stairFootprintFromDrag,
  subscribeObjectRegistry,
  ThumbnailRenderer,
  useMapEditorStore,
  wallFromDrag,
  type AudioZoneVoiceSpec,
  type BoneMapping,
  type CharacterDefinition,
  type ClientLogicRuntimeHandle,
  type LibraryAnimatedModel,
  type LogicEffect,
  type MapDecal,
  type PlatformerConfig,
  type MapDocument,
  type MapFloorDocument,
  type MapLight,
  type MapOpeningDraft,
  type MapRoomDraft,
  type MapRoomMarker,
  type MapStairRun,
  type MapStairTrigger,
  type MapStructurePreview,
  type MapTriggerVolume,
  type MapVoidDraft,
  type PrototypeClip,
  type PrototypeGltf,
  type StairFootprint,
  type StudioAssetRecord,
  ViewportHost,
} from '../../engine';
import type { MapProblem } from '@sstk/sunder-sim';
import { PlaytestOverlay } from '../playtest/PlaytestOverlay';
import { PlaytestValidationPanel } from '../playtest/PlaytestValidationPanel';
import { validatePlaytestDocument } from '../playtest/playtestValidation';
import { useScriptConsoleStore } from './scriptConsoleStore';
import { usePreviewScriptRuntime } from './usePreviewScriptRuntime';
import { ImageSlot } from '../components/ImageSlot';
import { Glyph } from '../components/ui-shared';
import { notYetLiveGameplayReadiness } from '../config/gameplayReadiness';
import { Link, navigate } from '../design/router';
import { Navigate } from 'react-router-dom';
import type { Group, SkinnedMesh } from 'three';
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter.js';
import { useProject } from '../project/ProjectProvider';
import { uploadGlbAsset } from '../assets/glbUpload';
import { saveCharacterRoster, saveMapDocument } from '../persistence/projectRepository';
import { useEditorHost } from './EditorHost';
import { ShortcutOverlay } from './ShortcutOverlay';
import { usePeerCursors } from './usePeerCursors';
import { PeerCursorsOverlay } from './PeerCursorsOverlay';
import { ViewportToolbar, ViewportBar, VtToolButton, VtChip, BottomDock, DockConsole, EdConfirmDialog, EditorStatusBar, GameRulesPanel, GameCameraPanel, GameWorldPanel, GameMetaPanel, GsSeg } from './editor-panels';
import { PixelRoute } from './editor-pixel';
import { ModelRoute } from './editor-model';
import {
  HudWidgetMock, HUD_WIDGETS, HUD_DOC_FALLBACK, formatMatchOutcome,
  AUDIO_DOC_FALLBACK, LOGIC_DOC_FALLBACK, hudTuning, migrateAudioDoc,
  type AudioDoc, type HudDoc, type HudLiveState, type LogicDoc,
} from './editor-modes2';
import { M2Panel, M2Label, M2ListRow, EdSlider, EdField, EdSelect, EdDangerButton, EdEmptyState } from './panelPrimitives';
import { useModeDocument } from './useModeDocument';
import { GAMESETUP_DOC_FALLBACK, type GameSetupDoc } from '../services/gameSetup';
import { useMapEditorToastBridge } from './useMapEditorToastBridge';
import { DELETE_CONFIRM_THRESHOLD, inEditableTarget, useEditorShortcuts } from './useEditorShortcuts';
import { CharactersRoute } from './editor-modes3';
import { SceneEnvironmentPanel } from './scenePanels';
import {
  FX_DOC_FALLBACK, GAMEDATA_DOC_FALLBACK, migrateFxDoc,
  type FxDoc, type GameDataDoc,
} from './editor-data-fx';
import { Workspace } from './panel-system/Workspace';
import { useMapSelectedRoomId } from './panel-system/selectionStores';
import type { PanelDescriptor, PresetDescriptor } from './panel-system/types';
import { GunsmithStudioPage } from '../gunsmith/GunsmithStudioPage';
import { CodeEditorPanel, CodeFileListPanel, CodeOutputPanel, CodeProblemsPanel, CodeReferencePanel } from './codePanels';
import {
  SystemsLogicPanel, SystemsHudPanel, SystemsAudioPanel, SystemsFxPanel,
  SystemsDataPanel, SystemsCutscenePanel, SystemsIntroPanel,
} from './systemsPanels';

export function EdNumber({
  label,
  value,
  onCommit,
  disabled,
  title,
  muted,
}: {
  label: string;
  value: string;
  /** Called with the raw field text on blur or Enter; omit for display-only. */
  onCommit?: (raw: string) => void;
  disabled?: boolean;
  /** Tooltip on the label - e.g. clarifying an axis's relevance in 2D. */
  title?: string;
  /** Dims the field without disabling it - still editable, just flagged as
   * usually irrelevant (e.g. a depth/height axis in a 2D top-down scene). */
  muted?: boolean;
}) {
  // Controlled with a focus-scoped draft: unfocused fields always show the
  // live store value (no stale numbers across selection changes), and typing
  // never gets clobbered mid-edit. Enter commits, Escape reverts.
  const [draft, setDraft] = useState<string | null>(null);
  const cancelled = useRef(false);
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minWidth: 0 }} title={title}>
      <span style={{ fontFamily: 'var(--ss-sans)', fontWeight: 500, fontSize: 9, letterSpacing: '0.12em', color: 'var(--ss-text-3)', textTransform: 'uppercase' }}>{label}</span>
      <input
        className="ss-input"
        value={draft ?? value}
        disabled={disabled}
        onFocus={() => setDraft(value)}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={(e) => {
          const revert = cancelled.current;
          cancelled.current = false;
          setDraft(null);
          if (!revert) onCommit?.(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          if (e.key === 'Escape') {
            cancelled.current = true;
            (e.target as HTMLInputElement).blur();
          }
        }}
        style={{ padding: '6px 8px', fontSize: 12.5, fontFamily: 'var(--ss-sans)', fontWeight: 500, borderRadius: 7, opacity: disabled ? 0.55 : muted ? 0.6 : 1 }}
      />
    </label>
  );
}

export function ViewportChip({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <span style={{
      position: 'absolute', display: 'inline-flex', alignItems: 'center', gap: 8,
      background: 'var(--ss-glass)', backdropFilter: 'blur(8px)',
      border: '1px solid var(--ss-border-soft)', borderRadius: 999,
      padding: '7px 14px', fontFamily: 'var(--ss-sans)', fontWeight: 500, fontSize: 'var(--ss-fs-2xs)', letterSpacing: '0.08em',
      color: 'var(--ss-text-2)', pointerEvents: 'none', whiteSpace: 'nowrap', ...style,
    }}>{children}</span>
  );
}

/* ── publish popover ──────────────────────────────────────────────────── */
const VIS_LABEL_TO_WIRE: Record<string, 'public' | 'unlisted' | 'private'> = {
  Public: 'public',
  Unlisted: 'unlisted',
  Off: 'private',
};

function PrePublishReadinessNote() {
  const notYetLive = notYetLiveGameplayReadiness();
  if (notYetLive.length === 0) return null;
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 6, padding: '10px 12px',
      border: '1px solid var(--ss-border-soft)', borderRadius: 9, background: 'var(--ss-bg-2)',
    }}>
      <span className="ss-field-label">Before you publish</span>
      <span style={{ fontSize: 11.5, color: 'var(--ss-text-3)', lineHeight: 1.5 }}>
        These authored systems will not affect gameplay yet: {notYetLive.map((entry) => entry.system).join(', ')}.
      </span>
    </div>
  );
}

export function PublishPopover({ onClose }: { onClose: () => void }) {
  const { projectId, project, workspaceBase } = useEditorHost();
  const [vis, setVis] = useState('Public');
  const [url, setUrl] = useState<string | null>(null);
  const [version, setVersion] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  // The version that just went live; flips the popover into its success state.
  const [justPublished, setJustPublished] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    void import('../services/publish').then(({ getPublication }) =>
      getPublication(projectId)
        .then((pub) => {
          if (cancelled || !pub) return;
          setUrl(pub.url.replace(/^https?:\/\//, ''));
          setVersion(pub.version);
          const label = Object.entries(VIS_LABEL_TO_WIRE).find(([, wire]) => wire === pub.visibility)?.[0];
          if (label) setVis(label);
        })
        // Non-critical pre-fill: a failed check just leaves the popover on
        // its "not yet published" default rather than surfacing an error.
        .catch((err: unknown) => console.error('[publish popover] failed to load publication status:', err)),
    );
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  async function publish() {
    setBusy(true);
    setError(null);
    try {
      // Keep the editing state alongside the publish version (best effort).
      void import('../persistence/projectRepository').then(({ createSceneSnapshot }) =>
        createSceneSnapshot(projectId, `Before publish v${(version ?? 0) + 1}`, {
          ...project.map,
          objectInstances: useMapEditorStore.getState().objectInstances,
        }).catch((err: unknown) => {
          console.error('[publish] scene snapshot failed:', err);
          toast('Published successfully, but scene thumbnail failed to save', 'warning');
        }),
      );
      const { publishProject } = await import('../services/publish');
      const pub = await publishProject(projectId, { visibility: VIS_LABEL_TO_WIRE[vis] });
      setUrl(pub.url.replace(/^https?:\/\//, ''));
      setVersion(pub.version);
      // The payoff moment: stay open and show the claimed URL.
      setJustPublished(pub.version);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Publish failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{
      position: 'absolute', top: 50, right: 0, width: 340, zIndex: 80,
      background: 'var(--ss-panel-strong)', border: '1px solid var(--ss-border)', borderRadius: 16,
      boxShadow: '0 30px 80px rgba(0,0,0,0.7)', padding: 22,
      display: 'flex', flexDirection: 'column', gap: 16,
    }} onClick={(e) => e.stopPropagation()}>
      {justPublished !== null ? (
        <Fragment>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '6px 0 2px', textAlign: 'center' }}>
            <span aria-hidden="true" style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--ss-accent-soft)', border: '1px solid var(--ss-accent-border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="18" height="18" viewBox="0 0 16 16" fill="none"><path d="M2.5 8.5l3.5 3.5 7.5-8" stroke="var(--ss-accent)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </span>
            <span style={{ fontSize: 15, fontWeight: 600 }}>v{justPublished} is live</span>
            <span style={{ fontSize: 12.5, color: 'var(--ss-text-3)' }}>Anyone with the link can play your game right now.</span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input className="ss-input" readOnly value={url ?? ''} style={{ fontFamily: 'var(--ss-mono)', fontSize: 12 }} />
            <button
              className="ss-btn ss-btn--ghost ss-btn--sm"
              style={{ borderRadius: 9 }}
              onClick={() => {
                if (!url) return;
                void navigator.clipboard?.writeText(url).catch(() => {});
                setCopied(true);
                setTimeout(() => setCopied(false), 1600);
              }}
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          {url && (
            <Link to={'/play/' + (url.split('/').pop() ?? '')} className="ss-btn ss-btn--primary" style={{ width: '100%', justifyContent: 'center' }}>
              View live game
            </Link>
          )}
          <button className="ss-btn ss-btn--ghost" style={{ width: '100%' }} onClick={onClose}>Done</button>
        </Fragment>
      ) : (
      <Fragment>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 15, fontWeight: 600 }}>Publish</span>
        <span style={{ fontSize: 12.5, color: 'var(--ss-text-3)' }}>Snapshot the project and update the live game.</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        <span className="ss-field-label">Visibility</span>
        <GsSeg listLabel="Project visibility" options={['Public', 'Unlisted', 'Off']} value={vis} onChange={setVis} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        <span className="ss-field-label">Play URL</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <input className="ss-input" readOnly value={url ?? 'Claimed on first publish'} style={{ fontFamily: 'var(--ss-mono)', fontSize: 12 }} />
          <button
            className="ss-btn ss-btn--ghost ss-btn--sm"
            style={{ borderRadius: 9 }}
            disabled={!url}
            onClick={() => {
              if (!url) return;
              void navigator.clipboard?.writeText(url).catch(() => {});
              setCopied(true);
              setTimeout(() => setCopied(false), 1600);
            }}
          >
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      </div>
      <PrePublishReadinessNote />
      {error && (
        <p style={{ color: 'var(--ss-danger)', fontSize: 12.5 }} role="alert">
          {error}
        </p>
      )}
      <button className="ss-btn ss-btn--primary" style={{ width: '100%' }} onClick={publish} disabled={busy}>
        {busy ? 'Publishing…' : `Publish v${(version ?? 0) + 1}`}
      </button>
      <Link to={`${workspaceBase}/publish`} style={{ fontSize: 12.5, color: 'var(--ss-text-3)', textAlign: 'center' }}>Manage hosting & versions</Link>
      </Fragment>
      )}
    </div>
  );
}

/* ── top bar (no tabs - modes live in the icon rail) ──────────────────── */
const SAVE_LABELS: Record<string, string> = {
  idle: 'Saved',
  saving: 'Saving…',
  saved: 'Saved',
  // Distinct from 'saved': only the transient backup slot was written, not
  // the source file - "Discard changes" would still discard this.
  autosaved: 'Backed up',
  error: 'Save failed',
};

export function EditorTopBar({ mode, projection, panels, distractionFree, onToggleDistractionFree }: { mode: string; projection: string; panels: readonly PanelDescriptor[]; distractionFree?: boolean; onToggleDistractionFree?: () => void }) {
  // Legacy short mode names (still valid, bookmarked URLs) that don't equal
  // a real panel id - kept only for those; a real panel id (which the URL
  // now also carries directly, since Workspace's onActivePanelChange keeps
  // the URL in sync with whichever specific panel is actually on screen)
  // resolves its label straight from the registry's own `title` below
  // instead, so a sub-panel like `logic.rule-editor` or `code.editor` gets a
  // correct, un-hand-maintained label rather than falling through to "Edit".
  const MODE_LABELS: Record<string, string> = { edit: 'Edit', map: 'Map Builder', environment: 'Environment', model: 'Model Studio', characters: 'Characters', animation: 'Animation Studio', pixel: 'Pixel Studio', logic: 'Logic', hud: 'HUD Builder', audio: 'Audio', fx: 'FX & Materials', data: 'Game Data', cutscene: 'Cutscenes', intro: 'Intro Sequences', game: 'Game Setup', preview: 'Preview', gunsmith: 'Gunsmith Kit', code: 'Code' };
  const modeLabel = panels.find((p) => p.id === mode)?.title ?? MODE_LABELS[mode] ?? 'Edit';
  const [pub, setPub] = useState(false);
  const { project, editorBase, workspaceBase, userInitials } = useEditorHost();
  const saveState = useMapEditorStore((state) => state.saveState);
  return (
    <div className="editor-topbar" style={{
      height: 52, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '0 14px 0 16px', borderBottom: '1px solid var(--ss-border-soft)', background: 'var(--ss-bg-2)', position: 'relative', zIndex: 70,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
        <Link to={`${editorBase}/edit`} title="Studio home: Edit mode" style={{ display: 'flex', alignItems: 'center' }}><Glyph size={26} /></Link>
        <span className="editor-topbar-chip" style={{ width: 1, height: 20, background: 'var(--ss-border-soft)' }} />
        <Link to={`${workspaceBase}/overview`} style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
          <span style={{ fontSize: 13.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{project.meta.name}</span>
          <span className="editor-topbar-chip" style={{ fontFamily: 'var(--ss-sans)', fontWeight: 500, fontSize: 9.5, letterSpacing: '0.12em', color: 'var(--ss-text-3)', border: '1px solid var(--ss-border-soft)', borderRadius: 999, padding: '3px 9px', textTransform: 'uppercase' }}>{project.meta.template}</span>
        </Link>
        <span className="editor-topbar-chip" style={{ fontFamily: 'var(--ss-sans)', fontWeight: 500, fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ss-text-3)', whiteSpace: 'nowrap' }}>
          {modeLabel}
        </span>
        <span className="editor-topbar-chip" title="Game dimension. Set at creation, changeable in Game Setup." style={{ fontFamily: 'var(--ss-sans)', fontWeight: 600, fontSize: 9, letterSpacing: '0.12em', color: 'var(--ss-accent)', border: '1px solid var(--ss-accent-border)', background: 'var(--ss-accent-soft)', borderRadius: 999, padding: '2px 8px', whiteSpace: 'nowrap' }}>{projection}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, position: 'relative' }}>
        {/* Edit mode's own manual save (Ctrl+S, useEditorShortcuts.ts) had no
            visible affordance for a mouse-only creator - this chip is scoped
            to mapEditorStore's saveState, which only Edit/Map Builder use, so
            the button only appears there rather than implying every mode has
            a manual save through this same store. */}
        {mode === 'edit' && (
          <button
            className="ss-btn ss-btn--ghost ss-btn--sm"
            onClick={() => void useMapEditorStore.getState().saveToDisk()}
            disabled={saveState === 'saving'}
            title="Save now (Ctrl/Cmd + S)"
            style={{ padding: '5px 12px', fontSize: 11.5, borderRadius: 8 }}
          >
            Save
          </button>
        )}
        <span className="editor-topbar-chip" style={{ fontFamily: 'var(--ss-sans)', fontWeight: 500, fontSize: 10.5, letterSpacing: '0.08em', color: saveState === 'error' ? 'var(--ss-danger)' : 'var(--ss-text-3)', whiteSpace: 'nowrap' }}>{SAVE_LABELS[saveState] ?? 'Saved'}</span>
        {mode !== 'preview' && onToggleDistractionFree && (
          <button
            className="ss-btn ss-btn--ghost ss-btn--sm"
            onClick={onToggleDistractionFree}
            aria-pressed={distractionFree}
            title={(distractionFree ? 'Exit' : 'Enter') + ' distraction-free mode (Ctrl/Cmd + Shift + F)'}
            style={{ padding: '5px 9px', fontSize: 11.5, borderRadius: 8, color: distractionFree ? 'var(--ss-accent)' : undefined }}
          >
            <svg width="13" height="13" viewBox="0 0 16 16" aria-hidden="true">
              <path d="M2 6V3.5A1.5 1.5 0 0 1 3.5 2H6M10 2h2.5A1.5 1.5 0 0 1 14 3.5V6M14 10v2.5a1.5 1.5 0 0 1-1.5 1.5H10M6 14H3.5A1.5 1.5 0 0 1 2 12.5V10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
            </svg>
          </button>
        )}
        {mode !== 'preview' && (
          <Link to={`${editorBase}/preview`} className="ss-btn ss-btn--ghost ss-btn--sm editor-topbar-chip" style={{ gap: 7 }}>
            <svg width="10" height="11" viewBox="0 0 10 11" fill="none" aria-hidden="true"><path d="M1.5 1.3v8.4l7.2-4.2-7.2-4.2z" fill="currentColor" /></svg>
            Preview
          </Link>
        )}
        <button className="ss-btn ss-btn--primary ss-btn--sm" onClick={() => setPub(!pub)}>Publish</button>
        <div className="app-avatar">{userInitials}</div>
        {pub && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 75 }} onClick={() => setPub(false)}>
            <div style={{ position: 'absolute', top: 52, right: 14 }}>
              <PublishPopover onClose={() => setPub(false)} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── mode rail (removed) ──────────────────────────────────────────────────
 * A persistent vertical icon rail (14 flat links across 4 divider groups)
 * used to live here, switching the whole workspace the way the top preset
 * tab strip (panel-system/Workspace.tsx's PresetSwitcher) now does. Removed:
 * no editor/game-authoring tool researched (Godot, Blender, Unity, Unreal,
 * Roblox Studio) uses a full-height vertical rail to switch the *entire*
 * workspace - that job sits in a small top tab strip everywhere it was
 * found. The one real precedent for a vertical icon rail, VS Code's
 * Activity Bar, only ever swaps a side panel's content, never the main
 * editor area, which isn't what this rail did. Legacy per-mode URLs and the
 * ModeRail-vs-fine-grained-panel distinction are unaffected: a bookmarked
 * `/app/editor/:id/:mode` link still resolves to the right preset+panel
 * (see ScreenEditor/Workspace's activePanelId handling); reaching a specific
 * panel without a bookmark now happens via the workspace's own tab strip or
 * the Panels menu, not a second, always-visible nav surface duplicating it.
 * ───────────────────────────────────────────────────────────────────────── */

/* ── Edit mode (default) ──────────────────────────────────────────────── */
type SceneNodeKind = 'group' | 'mesh' | 'light' | 'marker' | 'trigger' | 'decal' | 'structure';

interface SceneNode {
  id: string;
  name: string;
  kind: SceneNodeKind;
  depth: number;
  /** Set only for a 'structure' node: where clicking it should go, since
   * walls/panels live in the map document (Map Builder's own undo/delete
   * flow), not the object store this tree otherwise selects into. */
  href?: string;
}

/** The live scene from the map-editor store, grouped the way the design draws it. */
function useSceneNodes(filter: string): SceneNode[] {
  const instances = useMapEditorStore((state) => state.objectInstances);
  const lights = useMapEditorStore((state) => state.lights);
  const markers = useMapEditorStore((state) => state.markers);
  const triggers = useMapEditorStore((state) => state.triggers);
  const decals = useMapEditorStore((state) => state.decals);
  const { project, editorBase } = useEditorHost();
  const mapFloors = project.map.floors;
  return useMemo(() => {
    const q = filter.trim().toLowerCase();
    const match = (name: string) => !q || name.toLowerCase().includes(q);
    const nodes: SceneNode[] = [];
    const objects = instances.filter((i) => match(i.name ?? i.id));
    if (objects.length) {
      nodes.push({ id: '__objects', name: 'objects', kind: 'group', depth: 0 });
      nodes.push(...objects.map((i) => ({ id: i.id, name: i.name ?? i.id, kind: 'mesh' as const, depth: 1 })));
    }
    const lit = lights.filter((l) => match(l.id));
    if (lit.length) {
      nodes.push({ id: '__lighting', name: 'lighting', kind: 'group', depth: 0 });
      nodes.push(...lit.map((l) => ({ id: l.id, name: l.id, kind: 'light' as const, depth: 1 })));
    }
    const marks = markers.filter((m) => match(m.name || m.id));
    const trigs = triggers.filter((t) => match(t.id));
    if (marks.length || trigs.length) {
      nodes.push({ id: '__gameplay', name: 'gameplay', kind: 'group', depth: 0 });
      nodes.push(...marks.map((m) => ({ id: m.id, name: m.name || m.id, kind: 'marker' as const, depth: 1 })));
      nodes.push(...trigs.map((t) => ({ id: t.id, name: t.id, kind: 'trigger' as const, depth: 1 })));
    }
    const decs = decals.filter((d) => match(d.material + ' ' + d.id));
    if (decs.length) {
      nodes.push({ id: '__decals', name: 'decals', kind: 'group', depth: 0 });
      nodes.push(...decs.map((d) => ({ id: d.id, name: d.id, kind: 'decal' as const, depth: 1 })));
    }
    // Structure (walls/panels) lives in the map document, drawn in Map
    // Builder, not this store - listed here (read-only, one row per floor
    // that has any) so it stops being invisible background geometry that
    // only Map Builder's own Erase tool knows exists.
    const floorsWithStructure = mapFloors.filter((f) => (f.walls.length > 0 || f.panels.length > 0) && match(f.label));
    if (floorsWithStructure.length) {
      nodes.push({ id: '__structure', name: 'structure', kind: 'group', depth: 0 });
      nodes.push(...floorsWithStructure.map((f) => ({
        id: `__structure-${f.level}`,
        name: `${f.label} · ${f.panels.length} panel${f.panels.length === 1 ? '' : 's'}, ${f.walls.length} wall${f.walls.length === 1 ? '' : 's'}`,
        kind: 'structure' as const,
        depth: 1,
        href: `${editorBase}/map`,
      })));
    }
    return nodes;
  }, [instances, lights, markers, triggers, decals, mapFloors, editorBase, filter]);
}

export function SceneTree({ selected, onSelect, onDelete, filter = '' }: { selected: string; onSelect: (id: string) => void; onDelete?: (id: string) => void; filter?: string }) {
  const tree = useSceneNodes(filter);
  const { editorBase } = useEditorHost();
  const kindColor: Record<SceneNodeKind, string> = { group: 'var(--ss-text-3)', mesh: 'var(--ss-accent)', light: 'var(--ss-warning)', marker: 'var(--ss-info)', trigger: 'var(--ss-success)', decal: 'var(--ss-text-2)', structure: 'var(--ss-text-3)' };
  // Pinned to the bottom of the panel (`marginTop: auto` on a flex column)
  // rather than trailing the last tree row: with a short tree it used to
  // sit high up with a large unexplained gap under it, and the gap grew
  // with every row the scene lost.
  const hint = (
    <div style={{ marginTop: 'auto', padding: '14px 16px 4px', fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)', lineHeight: 1.55 }}>
      Floors, walls and panels are drawn in <Link to={`${editorBase}/map`}>Map Builder</Link> and appear here as collidable geometry automatically.
    </div>
  );
  if (tree.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <div style={{ padding: '4px 16px', fontSize: 'var(--ss-fs-sm)', color: 'var(--ss-text-3)', lineHeight: 1.55 }}>
          Nothing in the scene yet. Drag assets from the dock, or draw structure in Map Builder.
        </div>
        {hint}
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <div style={{ display: 'flex', flexDirection: 'column', padding: '0 8px' }}>
        {tree.map((n) => (
          <div key={n.id} style={{ display: 'flex', alignItems: 'center', borderRadius: 8, background: selected === n.id ? 'var(--ss-accent-soft)' : 'transparent' }}>
            {n.kind === 'structure' && n.href ? (
              <Link to={n.href} title="Open in Map Builder" style={{
                flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 9, padding: '6px 10px', paddingLeft: 10 + n.depth * 16,
                borderRadius: 8, textAlign: 'left', color: 'var(--ss-text-2)', fontSize: 'var(--ss-fs-sm)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: kindColor[n.kind], flexShrink: 0 }} />
                {n.name}
              </Link>
            ) : (
              <button onClick={() => n.kind !== 'group' && onSelect(n.id)} style={{
                flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 9, padding: '6px 10px', paddingLeft: 10 + n.depth * 16,
                border: 'none', borderRadius: 8, textAlign: 'left', background: 'transparent',
                color: selected === n.id ? 'var(--ss-text)' : 'var(--ss-text-2)',
                fontSize: 'var(--ss-fs-sm)', fontFamily: 'inherit',
                fontWeight: n.kind === 'group' ? 600 : 450,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                <span style={{ width: 6, height: 6, borderRadius: n.kind === 'group' ? 1 : '50%', background: kindColor[n.kind], flexShrink: 0 }} />
                {n.name}
              </button>
            )}
            {onDelete && n.kind === 'mesh' && selected === n.id && (
              <button
                onClick={() => onDelete(n.id)}
                title="Delete object (Del)"
                aria-label={'Delete ' + n.name}
                style={{
                  border: 'none', background: 'transparent', color: 'var(--ss-text-3)', fontSize: 13,
                  padding: '4px 9px', flexShrink: 0, lineHeight: 1, borderRadius: 6,
                }}
              >×</button>
            )}
          </div>
        ))}
      </div>
      {hint}
    </div>
  );
}


const RAD_TO_DEG = 180 / Math.PI;

/** Last cadence-snapshot time per project (session scope). */
const AUTO_SNAPSHOT_AT = new Map<string, number>();

export function EditRoute({ projection, onAddAsset }: { projection: string; onAddAsset?: () => void }) {
  const { project, projectId } = useEditorHost();
  const is2d = projection === '2D';
  const [filter, setFilter] = useState('');
  const selectedIds = useMapEditorStore((state) => state.selectedObjectIds);
  const selectObject = useMapEditorStore((state) => state.selectObject);
  const instances = useMapEditorStore((state) => state.objectInstances);
  const updateObject = useMapEditorStore((state) => state.updateObject);
  const updateObjectParam = useMapEditorStore((state) => state.updateObjectParam);
  const saveToDisk = useMapEditorStore((state) => state.saveToDisk);
  const deleteSelected = useMapEditorStore((state) => state.deleteSelected);
  const lights = useMapEditorStore((state) => state.lights);
  const markers = useMapEditorStore((state) => state.markers);
  const triggers = useMapEditorStore((state) => state.triggers);
  const decals = useMapEditorStore((state) => state.decals);
  const moveObject = useMapEditorStore((state) => state.moveObject);
  // Material presets authored in the FX mode drive the inspector's selector.
  const { data: fxStored } = useModeDocument<FxDoc>('fx', FX_DOC_FALLBACK);
  const fxMaterials = useMemo(() => migrateFxDoc(fxStored).materials, [fxStored]);

  // Grid toggle: persisted in session storage so it survives mode switches.
  const [showGrid, setShowGrid] = useState(() => {
    try { return localStorage.getItem('ss-editor-grid') !== 'false'; } catch { return true; }
  });
  const toggleGrid = useCallback(() => {
    setShowGrid((v) => {
      try { localStorage.setItem('ss-editor-grid', String(!v)); } catch { /* ignore */ }
      return !v;
    });
  }, []);

  // Deletes confirm above the threshold; the dialog drives deleteSelected.
  const [confirmDeleteCount, setConfirmDeleteCount] = useState<number | null>(null);
  const requestDelete = useCallback(() => {
    const count = useMapEditorStore.getState().selectedObjectIds.length;
    if (count === 0) return;
    if (count > DELETE_CONFIRM_THRESHOLD) {
      setConfirmDeleteCount(count);
      return;
    }
    deleteSelected();
  }, [deleteSelected]);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const toggleShortcuts = useCallback(() => setShowShortcuts((v) => !v), []);
  const peerCursors = usePeerCursors(projectId);
  useEditorShortcuts(requestDelete, toggleShortcuts);

  // In-editor combat playtest: launches a real Sunder match (via
  // @sstk/sunder-sim) against the map currently open in the editor. See
  // docs/in-editor-combat-playtest-plan.md. The editor is never unmounted -
  // `playtestDocument` just mounts a full-viewport sibling overlay on top of
  // it, so exiting is a plain state reset with nothing to restore.
  const [playtestDocument, setPlaytestDocument] = useState<MapDocument | null>(null);
  const [playtestProblems, setPlaytestProblems] = useState<MapProblem[] | null>(null);
  const startPlaytest = useCallback(() => {
    const liveDoc = buildLiveMapDocument(useMapEditorStore.getState());
    if (!liveDoc) return;
    const result = validatePlaytestDocument(liveDoc);
    if (!result.ok) {
      setPlaytestProblems(result.problems);
      return;
    }
    setPlaytestDocument(liveDoc);
  }, []);

  useEffect(() => {
    const store = useMapEditorStore.getState();
    store.setEnabled(true);
    store.setInteractionMode('edit');
    return () => {
      useMapEditorStore.getState().setEnabled(false);
    };
  }, []);

  // Auto-save scene edits through the map persistence (the legacy overlay's
  // behaviour); without it, placed objects only live in the store. Every half
  // hour of editing also drops a scene snapshot, the cross-session safety net
  // undo cannot give.
  // Dirty across all collections so spawn-team / bomb-site edits autosave too.
  const dirty = useMapEditorStore(isStoreDirty);
  const dirtyRef = useRef(dirty);
  useEffect(() => {
    dirtyRef.current = dirty;
  }, [dirty]);

  const pendingAutosaveRef = useRef(false);
  useEffect(() => {
    if (!dirty) return;
    const timer = window.setTimeout(() => {
      pendingAutosaveRef.current = false;
      void saveToDisk({ autosave: true });
      const last = AUTO_SNAPSHOT_AT.get(projectId) ?? 0;
      if (Date.now() - last > 30 * 60 * 1000) {
        AUTO_SNAPSHOT_AT.set(projectId, Date.now());
        void import('../persistence/projectRepository').then(({ createSceneSnapshot }) =>
          createSceneSnapshot(projectId, 'Auto', { ...project.map, objectInstances: useMapEditorStore.getState().objectInstances }).catch((err: unknown) => {
            console.error('[auto-save] scene snapshot failed:', err);
            // The primary autosave two lines up still has its own visible
            // save-state chip on failure, so this isn't a total loss of
            // feedback - but the sibling snapshot at publish time (above)
            // already toasts on the identical failure, and this is the same
            // cross-session safety net silently not getting written.
            toast('Auto-save succeeded, but the scene history snapshot failed to save', 'warning');
          }),
        );
      }
    }, 2500);
    pendingAutosaveRef.current = true;
    return () => window.clearTimeout(timer);
  }, [dirty, instances, saveToDisk, projectId, project.map]);

  // Flush a still-pending debounced autosave, and warn before closing with
  // unsaved work. Previously the debounce timer above was simply cancelled
  // on unmount with nothing sent - switching editor modes within the 2.5s
  // window silently dropped the edit - and there was no beforeunload guard
  // anywhere in the shipped shell, so closing the tab gave no warning at all.
  useEffect(() => {
    const flushPending = (keepalive: boolean) => {
      if (!pendingAutosaveRef.current) return;
      pendingAutosaveRef.current = false;
      void saveToDisk({ autosave: true, keepalive });
    };
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!dirtyRef.current) return;
      flushPending(true);
      event.preventDefault();
      event.returnValue = '';
    };
    const onPageHide = () => flushPending(true);
    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('pagehide', onPageHide);
      // True unmount (leaving Edit mode, or the editor entirely) - not every
      // dependency-driven re-run of the debounce effect above.
      flushPending(false);
    };
  }, [saveToDisk]);

  const selected = selectedIds[0] ?? '';
  const inst = instances.find((i) => i.id === selected) ?? null;
  // Collidable is a fact of the asset: blockers carve the walkable mesh.
  const collidable = inst ? Boolean(getObjectDefinition(inst.assetId)?.bounds.blockerFootprint) : null;
  const pos = inst?.transform.position ?? [0, 0, 0];
  const rot = inst?.transform.rotation ?? [0, 0, 0];
  const scl = inst?.transform.scale ?? [1, 1, 1];
  const matName = typeof inst?.params?.material === 'string' ? inst.params.material : '';
  const roughnessPct = Math.round((typeof inst?.params?.roughness === 'number' ? inst.params.roughness : 0.8) * 100);
  const emissivePct = Math.round((typeof inst?.params?.emissive === 'number' ? inst.params.emissive : 0) * 100);

  // Non-prop entity selections from the scene tree.
  const selectedLight: MapLight | null = inst ? null : (lights.find((l) => l.id === selected) ?? null);
  const selectedMarker: MapRoomMarker | null = inst ? null : (markers.find((m) => m.id === selected) ?? null);
  const selectedTrigger: MapTriggerVolume | null = inst ? null : (triggers.find((t) => t.id === selected) ?? null);
  const selectedDecal: MapDecal | null = inst ? null : (decals.find((d) => d.id === selected) ?? null);
  const hasSelection = Boolean(inst ?? selectedLight ?? selectedMarker ?? selectedTrigger ?? selectedDecal);

  function commitDecalRotation(axis: 0 | 1 | 2, raw: string) {
    if (!selectedDecal) return;
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return;
    const next: [number, number, number] = [...(selectedDecal.rotation ?? [0, 0, 0])];
    next[axis] = parsed / RAD_TO_DEG;
    updateObjectParam(selectedDecal.id, 'rotation', next);
  }

  function commitLightPos(axis: 0 | 1 | 2, raw: string) {
    if (!selectedLight) return;
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return;
    const next: [number, number, number] = [...selectedLight.position];
    next[axis] = parsed;
    moveObject(selectedLight.id, next);
  }

  function applyMaterial(name: string) {
    if (!inst) return;
    const preset = fxMaterials.find((m) => m.name === name);
    if (!preset) return;
    updateObjectParam(inst.id, 'material', preset.name);
    updateObjectParam(inst.id, 'color', preset.colour);
    updateObjectParam(inst.id, 'roughness', preset.roughness);
    updateObjectParam(inst.id, 'metalness', preset.metalness);
    updateObjectParam(inst.id, 'emissive', preset.emissive);
    // Texture maps (docs/3d-content-pipeline/roadmap.md 3.2 Phase 1) - null
    // clears a placed instance's own override back to "unset" (the same
    // thing switching to a preset with no map already does for the other
    // fields is skipped for, since scalars always have a concrete value);
    // gltfMaterialOverrides only treats a string as "set".
    updateObjectParam(inst.id, 'albedoMap', preset.albedoMap ?? null);
    updateObjectParam(inst.id, 'normalMap', preset.normalMap ?? null);
    updateObjectParam(inst.id, 'roughnessMap', preset.roughnessMap ?? null);
    updateObjectParam(inst.id, 'metalnessMap', preset.metalnessMap ?? null);
  }

  function commitTransform(part: 'position' | 'rotation' | 'scale', axis: 0 | 1 | 2, raw: string) {
    if (!inst) return;
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return;
    const value = part === 'rotation' ? parsed / RAD_TO_DEG : parsed;
    updateObject(
      inst.id,
      (object) => {
        const current =
          part === 'position'
            ? object.transform.position
            : part === 'rotation'
              ? (object.transform.rotation ?? [0, 0, 0])
              : (object.transform.scale ?? [1, 1, 1]);
        const next: [number, number, number] = [current[0], current[1], current[2]];
        next[axis] = value;
        return { ...object, transform: { ...object.transform, [part]: next } };
      },
      'Edit transform',
    );
  }

  return (
    <>
    {/* `minWidth: 0` matters: without it this row's automatic minimum size is
        its own min-content width, so the two fixed side columns plus the
        viewport bar's natural width pushed the row wider than the panel it
        sits in (measured: a 1237px row inside a 1024px dock, clipping the
        Inspector off the right edge). With it, the row is bounded by the
        dock and the viewport column absorbs the difference - which is also
        what lets the viewport bar's ResizeObserver see a genuinely narrow
        bar and collapse its trailing groups into the overflow menu. */}
    <div style={{ flex: 1, display: 'flex', minWidth: 0, minHeight: 0 }}>
      <M2Panel side="left">
        <div style={{ padding: '14px 16px 0' }}>
          <input className="ss-input" placeholder="Search scene" value={filter} onChange={(e) => setFilter(e.target.value)} style={{ padding: '8px 11px', fontSize: 'var(--ss-fs-sm)', borderRadius: 9 }} />
        </div>
        <M2Label action={<button className="ss-btn ss-btn--ghost" title="Open the asset dock; drag assets into the viewport" onClick={onAddAsset} style={{ padding: '3px 10px', fontSize: 11, borderRadius: 7 }}>+ Add</button>}>Scene</M2Label>
        <SceneTree selected={selected} onSelect={(id) => selectObject(id)} onDelete={(id) => { selectObject(id); requestDelete(); }} filter={filter} />
      </M2Panel>
      {/* Viewport column: a docked toolbar row above the canvas, not an
          overlay floating on top of it. The toolbar used to be an
          absolutely-positioned pill centred on this container, sized purely
          by its own content - at Scene's own measured column widths that
          made it 697px wide over a 538px viewport, painting 79px over the
          scene tree to its left and 79px over the Inspector to its right.
          Godot, Unreal and Unity all dock the viewport toolbar above the
          view for exactly this reason. */}
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, minHeight: 0 }}>
        <ViewportToolbar dim={projection} showGrid={showGrid} onToggleGrid={toggleGrid} />
        <div style={{ position: 'relative', flex: 1, minHeight: 0, minWidth: 0, background: is2d ? '#060607' : 'radial-gradient(ellipse 70% 60% at 50% 40%, var(--ss-surface), var(--ss-bg-2) 80%)' }}>
          {is2d && (
            <div style={{
              position: 'absolute', inset: 0,
              backgroundImage: 'linear-gradient(var(--ss-accent-soft) 1px, transparent 1px), linear-gradient(90deg, var(--ss-accent-soft) 1px, transparent 1px)',
              backgroundSize: '36px 36px', opacity: 0.55,
            }} />
          )}
          <GameRuntime
            mapDocument={project.map}
            config={project.config}
            characters={project.characters}
            poseLibraries={project.poseLibraries}
            mode="edit"
            projection={is2d ? '2D' : '3D'}
            chrome="viewport"
            showGrid={!is2d && showGrid}
          />
          <ViewportChip style={{ top: 14, left: 14 }}>{is2d ? `${project.meta.name.toLowerCase().replace(/\s+/g, '_')}.scene · top view` : `${project.meta.name.toLowerCase().replace(/\s+/g, '_')}.scene`}</ViewportChip>
          <ViewportChip style={{ bottom: 14, left: '50%', transform: 'translateX(-50%)' }}>{is2d ? 'Drag to pan · Scroll zooms · G grab' : 'FLY → WASD · F focus · G grab · ? shortcuts'}</ViewportChip>
          {!is2d && (
            <button
              type="button"
              className="ss-btn ss-btn--primary"
              onClick={startPlaytest}
              title="Launch a bot match against this map without leaving the editor"
              style={{ position: 'absolute', top: 14, right: 14, pointerEvents: 'auto', padding: '6px 14px', fontSize: 12, borderRadius: 999 }}
            >
              ▶ Playtest
            </button>
          )}
          <PeerCursorsOverlay peers={peerCursors} />
          <ShortcutOverlay open={showShortcuts} onClose={() => setShowShortcuts(false)} />
        </div>
      </div>
      <M2Panel side="right">
        <M2Label>Inspector</M2Label>
        {/* ── Object instance inspector ────────────────────────────── */}
        {inst && (
          <Fragment>
            <div style={{ padding: '0 16px', marginBottom: 16 }}>
              <input
                className="ss-input"
                key={selected}
                defaultValue={inst.name ?? inst.id}
                onBlur={(e) => updateObject(inst.id, (object) => ({ ...object, name: e.target.value.trim() || undefined }), 'Rename object')}
                style={{ fontFamily: 'var(--ss-sans)', fontWeight: 500, fontSize: 'var(--ss-fs-sm)', padding: '8px 11px', borderRadius: 9 }}
              />
            </div>
            <M2Label>Transform</M2Label>
            <div key={selected} style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '0 16px', marginBottom: 6 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <EdNumber label="Pos X" value={pos[0].toFixed(2)} onCommit={(raw) => commitTransform('position', 0, raw)} />
                <EdNumber label="Y" value={pos[1].toFixed(2)} onCommit={(raw) => commitTransform('position', 1, raw)}
                  muted={is2d} title={is2d ? 'Height - the engine is Y-up, so this sits above/below the 2D ground plane rather than on it' : undefined} />
                <EdNumber label="Z" value={pos[2].toFixed(2)} onCommit={(raw) => commitTransform('position', 2, raw)} />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <EdNumber label="Rot X" value={Math.round(rot[0] * RAD_TO_DEG).toString()} onCommit={(raw) => commitTransform('rotation', 0, raw)}
                  muted={is2d} title={is2d ? 'A tilt out of the 2D ground plane - usually irrelevant for a top-down scene' : undefined} />
                <EdNumber label="Y" value={Math.round(rot[1] * RAD_TO_DEG).toString()} onCommit={(raw) => commitTransform('rotation', 1, raw)}
                  title={is2d ? 'Facing direction in the 2D ground plane - the one rotation axis that visibly matters here' : undefined} />
                <EdNumber label="Z" value={Math.round(rot[2] * RAD_TO_DEG).toString()} onCommit={(raw) => commitTransform('rotation', 2, raw)}
                  muted={is2d} title={is2d ? 'A tilt out of the 2D ground plane - usually irrelevant for a top-down scene' : undefined} />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <EdNumber label="Scl X" value={scl[0].toFixed(2)} onCommit={(raw) => commitTransform('scale', 0, raw)} />
                <EdNumber label="Y" value={scl[1].toFixed(2)} onCommit={(raw) => commitTransform('scale', 1, raw)}
                  muted={is2d} title={is2d ? 'Height - the engine is Y-up, so this does not change the on-screen footprint of a 2D top-down object' : undefined} />
                <EdNumber label="Z" value={scl[2].toFixed(2)} onCommit={(raw) => commitTransform('scale', 2, raw)} />
              </div>
            </div>
            <M2Label>Material</M2Label>
            <div style={{ padding: '0 16px', marginBottom: 8 }}>
              <select className="ss-input ed-select" value={matName} onChange={(e) => applyMaterial(e.target.value)} aria-label="Material">
                <option value="">Object default</option>
                {fxMaterials.map((m) => <option key={m.name} value={m.name}>{m.name}</option>)}
              </select>
            </div>
            <EdSlider label="Emissive" suffix="%" value={emissivePct}
              onChange={(v) => updateObjectParam(inst.id, 'emissive', v / 100)} />
            <EdSlider label="Roughness" suffix="%" value={roughnessPct}
              onChange={(v) => updateObjectParam(inst.id, 'roughness', v / 100)} />
            <M2Label>Gameplay</M2Label>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 16px 20px', fontSize: 12.5, color: 'var(--ss-text-2)' }}>
              <span title="Whether this asset blocks movement; set by the asset's blocker footprint">Collidable</span>
              <span style={{
                fontFamily: 'var(--ss-sans)', fontWeight: 500, fontSize: 10, letterSpacing: '0.12em', borderRadius: 999, padding: '3px 10px',
                color: collidable ? 'var(--ss-accent)' : 'var(--ss-text-3)',
                border: '1px solid ' + (collidable ? 'var(--ss-accent-border)' : 'var(--ss-border-soft)'),
              }}>{collidable === null ? '-' : collidable ? 'ON' : 'OFF'}</span>
            </div>
            <EdDangerButton label="Delete object" onClick={requestDelete} title="Delete selected (Del)" />
          </Fragment>
        )}
        {/* ── Empty state: nothing selected at all ─────────────────── */}
        {!hasSelection && (
          <EdEmptyState
            icon={
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <rect x="3.5" y="3.5" width="17" height="17" rx="3" stroke="currentColor" strokeWidth="1.4" strokeDasharray="3 3" />
                <path d="M9 12h6M12 9v6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
              </svg>
            }
            title="Nothing selected"
            hint="Click an object in the viewport, or pick one from the scene tree, to edit its properties here."
          />
        )}
        {/* ── Light inspector ──────────────────────────────────────── */}
        {selectedLight && (
          <Fragment>
            <div style={{ padding: '0 16px 16px', fontFamily: 'var(--ss-mono)', fontSize: 11.5, color: 'var(--ss-text-3)' }}>{selectedLight.id}</div>
            <M2Label>Position</M2Label>
            <div key={selected} style={{ display: 'flex', gap: 8, padding: '0 16px', marginBottom: 6 }}>
              <EdNumber label="X" value={selectedLight.position[0].toFixed(2)} onCommit={(raw) => commitLightPos(0, raw)} />
              <EdNumber label="Y" value={selectedLight.position[1].toFixed(2)} onCommit={(raw) => commitLightPos(1, raw)} />
              <EdNumber label="Z" value={selectedLight.position[2].toFixed(2)} onCommit={(raw) => commitLightPos(2, raw)} />
            </div>
            <M2Label>Light</M2Label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '0 16px', marginBottom: 6 }}>
              <EdSelect label="Kind" value={selectedLight.kind} onChange={(value) => updateObjectParam(selectedLight.id, 'kind', value)}>
                  <option value="point">Point</option>
                  <option value="spot">Spot</option>
                  <option value="ambient-override">Ambient override</option>
                </EdSelect>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 'var(--ss-fs-2xs)', color: 'var(--ss-text-3)', flexShrink: 0 }}>Colour</span>
                <label style={{ position: 'relative', width: 34, height: 28, borderRadius: 7, overflow: 'hidden', border: '1px solid var(--ss-border)', cursor: 'pointer', background: selectedLight.color, flexShrink: 0 }}>
                  <input type="color" value={selectedLight.color}
                    onChange={(e) => updateObjectParam(selectedLight.id, 'color', e.target.value)}
                    style={{ position: 'absolute', inset: -8, opacity: 0, cursor: 'pointer' }} />
                </label>
                <input className="ss-input" value={selectedLight.color}
                  onChange={(e) => { if (/^#[0-9a-f]{6}$/i.test(e.target.value)) updateObjectParam(selectedLight.id, 'color', e.target.value); }}
                  style={{ fontFamily: 'var(--ss-mono)', fontSize: 12, padding: '6px 9px', borderRadius: 9 }} />
              </div>
            </div>
            <EdSlider label="Intensity" suffix="" value={Math.round(selectedLight.intensity * 10)} min={0} max={100}
              onChange={(v) => updateObjectParam(selectedLight.id, 'intensity', v / 10)} />
            {selectedLight.kind !== 'ambient-override' && (
              <EdSlider label="Range" suffix="m" value={Math.round(selectedLight.range ?? 10)} min={1} max={50}
                onChange={(v) => updateObjectParam(selectedLight.id, 'range', v)} />
            )}
            {selectedLight.kind === 'spot' && (
              <EdSlider label="Angle" suffix="°" value={Math.round((selectedLight.angle ?? Math.PI / 4) * RAD_TO_DEG)} min={5} max={90}
                onChange={(v) => updateObjectParam(selectedLight.id, 'angle', v / RAD_TO_DEG)} />
            )}
            {selectedLight.kind !== 'ambient-override' && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 16px 12px', fontSize: 12.5, color: 'var(--ss-text-2)' }}>
                <span>Cast shadow</span>
                <button onClick={() => updateObjectParam(selectedLight.id, 'castShadow', !selectedLight.castShadow)}
                  style={{
                    fontFamily: 'var(--ss-sans)', fontWeight: 500, fontSize: 10, letterSpacing: '0.12em', borderRadius: 999, padding: '3px 10px', cursor: 'pointer', border: '1px solid',
                    color: selectedLight.castShadow ? 'var(--ss-accent)' : 'var(--ss-text-3)',
                    borderColor: selectedLight.castShadow ? 'var(--ss-accent-border)' : 'var(--ss-border-soft)',
                    background: 'transparent',
                  }}>{selectedLight.castShadow ? 'ON' : 'OFF'}</button>
              </div>
            )}
            <EdDangerButton label="Delete light" onClick={requestDelete} title="Delete selected (Del)" />
          </Fragment>
        )}
        {/* ── Marker inspector ─────────────────────────────────────── */}
        {selectedMarker && (
          <Fragment>
            <div style={{ padding: '0 16px', marginBottom: 16 }}>
              <input className="ss-input" key={selected} defaultValue={selectedMarker.name || selectedMarker.id}
                onBlur={(e) => updateObjectParam(selectedMarker.id, 'name', e.target.value.trim() || selectedMarker.id)}
                style={{ fontFamily: 'var(--ss-sans)', fontWeight: 500, fontSize: 12.5, padding: '8px 11px', borderRadius: 9 }} />
            </div>
            <M2Label>Marker</M2Label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '0 16px', marginBottom: 16 }}>
              <EdSelect label="Kind" value={selectedMarker.kind} onChange={(value) => updateObjectParam(selectedMarker.id, 'kind', value)}>
                  <option value="spawn">Spawn</option>
                  <option value="checkpoint">Checkpoint</option>
                  <option value="event">Event</option>
                  <option value="custom">Custom</option>
                </EdSelect>
              {selectedMarker.kind === 'spawn' && (
                <EdSelect label="Team · Sunder" value={typeof selectedMarker.metadata?.['team'] === 'string' ? selectedMarker.metadata['team'] : ''} onChange={(value) => updateObjectParam(selectedMarker.id, 'team', value || undefined)}>
                    <option value="">- unassigned -</option>
                    <option value="raiders">Raiders (attack)</option>
                    <option value="wardens">Wardens (defend)</option>
                </EdSelect>
              )}
            </div>
            <div style={{ padding: '0 16px', fontSize: 12, color: 'var(--ss-text-3)', lineHeight: 1.55, marginBottom: 16 }}>
              Floor {selectedMarker.floor} · {selectedMarker.position[0].toFixed(1)}, {selectedMarker.position[1].toFixed(1)}
            </div>
            <EdDangerButton label="Delete marker" onClick={requestDelete} title="Delete selected (Del)" />
          </Fragment>
        )}
        {/* ── Trigger inspector ────────────────────────────────────── */}
        {selectedTrigger && (
          <Fragment>
            <div style={{ padding: '0 16px 16px', fontFamily: 'var(--ss-mono)', fontSize: 11.5, color: 'var(--ss-text-3)' }}>{selectedTrigger.id}</div>
            <M2Label>Trigger</M2Label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '0 16px', marginBottom: 6 }}>
              <EdSelect label="Kind" value={selectedTrigger.kind} onChange={(value) => updateObjectParam(selectedTrigger.id, 'kind', value)}>
                  <option value="teleport">Teleport</option>
                  <option value="event">Event</option>
                  <option value="damage">Damage</option>
                  <option value="door">Door</option>
                  <option value="radio">Radio</option>
                  <option value="jumpscare">Jumpscare</option>
                  <option value="custom">Custom</option>
                </EdSelect>
              <div style={{ display: 'flex', gap: 8 }}>
                <EdNumber label="Width" value={selectedTrigger.size[0].toFixed(1)}
                  onCommit={(raw) => { const v = Number(raw); if (Number.isFinite(v) && v > 0) updateObjectParam(selectedTrigger.id, 'size', [v, selectedTrigger.size[1]]); }} />
                <EdNumber label="Depth" value={selectedTrigger.size[1].toFixed(1)}
                  onCommit={(raw) => { const v = Number(raw); if (Number.isFinite(v) && v > 0) updateObjectParam(selectedTrigger.id, 'size', [selectedTrigger.size[0], v]); }} />
                <EdNumber label="H" value={selectedTrigger.height.toFixed(1)}
                  onCommit={(raw) => { const v = Number(raw); if (Number.isFinite(v) && v > 0) updateObjectParam(selectedTrigger.id, 'height', v); }} />
              </div>
              {(selectedTrigger.kind === 'event' || selectedTrigger.kind === 'custom' || selectedTrigger.kind === 'radio') && (
                <EdField label="Event name">
                  <input className="ss-input" key={selectedTrigger.id} defaultValue={selectedTrigger.eventName ?? ''}
                    placeholder="event_name"
                    onBlur={(e) => updateObjectParam(selectedTrigger.id, 'eventName', e.target.value.trim() || undefined)}
                    style={{ fontSize: 'var(--ss-fs-sm)', padding: '7px 10px', borderRadius: 9 }} />
                </EdField>
              )}
              {selectedTrigger.kind === 'event' && (
                <EdSelect label="Bomb site · Sunder" value={typeof selectedTrigger.params?.['site'] === 'string' ? selectedTrigger.params['site'] : ''} onChange={(value) => updateObjectParam(selectedTrigger.id, 'site', value || undefined)}>
                    <option value="">- not a bomb site -</option>
                    <option value="A">Site A</option>
                    <option value="B">Site B</option>
                </EdSelect>
              )}
              {selectedTrigger.kind === 'teleport' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <span style={{ fontSize: 'var(--ss-fs-2xs)', color: 'var(--ss-text-3)' }}>Target</span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <EdNumber label="Floor" value={String(selectedTrigger.targetFloor ?? 0)}
                      onCommit={(raw) => {
                        const v = Number(raw);
                        if (Number.isFinite(v)) updateObjectParam(selectedTrigger.id, 'targetFloor', Math.round(v));
                      }} />
                    <EdNumber label="X" value={(selectedTrigger.targetPosition?.[0] ?? 0).toFixed(1)}
                      onCommit={(raw) => {
                        const v = Number(raw);
                        if (Number.isFinite(v)) updateObjectParam(selectedTrigger.id, 'targetPosition', [v, selectedTrigger.targetPosition?.[1] ?? 0]);
                      }} />
                    <EdNumber label="Z" value={(selectedTrigger.targetPosition?.[1] ?? 0).toFixed(1)}
                      onCommit={(raw) => {
                        const v = Number(raw);
                        if (Number.isFinite(v)) updateObjectParam(selectedTrigger.id, 'targetPosition', [selectedTrigger.targetPosition?.[0] ?? 0, v]);
                      }} />
                  </div>
                </div>
              )}
            </div>
            <EdDangerButton label="Delete trigger" onClick={requestDelete} title="Delete selected (Del)" />
          </Fragment>
        )}
        {/* ── Decal inspector ──────────────────────────────────────── */}
        {selectedDecal && (
          <Fragment>
            <div style={{ padding: '0 16px 16px', fontFamily: 'var(--ss-mono)', fontSize: 11.5, color: 'var(--ss-text-3)' }}>{selectedDecal.id}</div>
            <M2Label>Decal</M2Label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '0 16px', marginBottom: 6 }}>
              <EdSelect label="Material" value={selectedDecal.material} onChange={(value) => updateObjectParam(selectedDecal.id, 'material', value)}>
                  <option value="wet_floor">Wet Floor</option>
                  <option value="stain">Floor Stain</option>
                  <option value="crack">Wall Crack</option>
                  {!['wet_floor', 'stain', 'crack'].includes(selectedDecal.material) && (
                    <option value={selectedDecal.material}>{selectedDecal.material}</option>
                  )}
                </EdSelect>
              <div style={{ display: 'flex', gap: 8 }}>
                <EdNumber label="Rot X" value={Math.round((selectedDecal.rotation?.[0] ?? 0) * RAD_TO_DEG).toString()} onCommit={(raw) => commitDecalRotation(0, raw)} />
                <EdNumber label="Y" value={Math.round((selectedDecal.rotation?.[1] ?? 0) * RAD_TO_DEG).toString()} onCommit={(raw) => commitDecalRotation(1, raw)} />
                <EdNumber label="Z" value={Math.round((selectedDecal.rotation?.[2] ?? 0) * RAD_TO_DEG).toString()} onCommit={(raw) => commitDecalRotation(2, raw)} />
              </div>
            </div>
            <EdSlider label="Opacity" suffix="%" value={Math.round((selectedDecal.opacity ?? 0.7) * 100)} min={0} max={100}
              onChange={(v) => updateObjectParam(selectedDecal.id, 'opacity', v / 100)} />
            <EdDangerButton label="Delete decal" onClick={requestDelete} title="Delete selected (Del)" />
          </Fragment>
        )}
      </M2Panel>
      {confirmDeleteCount !== null && (
        <EdConfirmDialog
          title={'Delete ' + confirmDeleteCount + ' objects?'}
          body={'This removes ' + confirmDeleteCount + ' objects from the scene. Undo with Ctrl+Z.'}
          confirmLabel={'Delete ' + confirmDeleteCount}
          onConfirm={() => { deleteSelected(); setConfirmDeleteCount(null); }}
          onCancel={() => setConfirmDeleteCount(null)}
        />
      )}
    </div>
    {playtestProblems && (
      <PlaytestValidationPanel problems={playtestProblems} onClose={() => setPlaytestProblems(null)} />
    )}
    {playtestDocument && (
      <PlaytestOverlay mapDocument={playtestDocument} onExit={() => setPlaytestDocument(null)} />
    )}
    </>
  );
}

/* ── Map Builder (structural: floors, panels, walls) ──────────────────── */
type BlueprintDrag = { from: [number, number]; to: [number, number]; freeAngle: boolean };
/** One structural change, for Map Builder's own undo stack. */
interface StructureUndoEntry {
  floors: MapFloorDocument[];
  stairRuns: MapStairRun[];
  stairTriggers: MapStairTrigger[];
  label: string;
}

export function MapRoute({ projection }: { projection: string }) {
  const { project, projectId, editorBase } = useEditorHost();
  const { setProject } = useProject();
  const instances = useMapEditorStore((state) => state.objectInstances);
  const is3d = projection !== '2D';
  const mapFloors = project.map.floors;
  const [activeLevel, setActiveLevel] = useState(() => mapFloors[0]?.level ?? 0);
  const activeFloor = mapFloors.find((f) => f.level === activeLevel) ?? mapFloors[0] ?? null;
  const [tool, setTool] = useState('Draw wall');
  const [wallHeight, setWallHeight] = useState(3.2);
  const [gridSnap, setGridSnap] = useState(0.5);
  const [thicknessCm, setThicknessCm] = useState(24);
  const [drag, setDrag] = useState<BlueprintDrag | null>(null);
  // Shared (docs/editor-consolidation/roadmap.md Phase 2): previously a
  // plain local useState, which meant this couldn't survive Map Builder
  // splitting into sibling panels and wasn't readable by the cross-panel
  // Inspector. Same createTransientSelectionStore pattern Logic/HUD/Audio/
  // Data/FX already use (see panel-system/selectionStores.ts).
  const [selectedRoomId, setSelectedRoomId] = useMapSelectedRoomId.use();
  const [stairKind, setStairKind] = useState<'run' | 'trigger'>('run');
  const [stairToLevel, setStairToLevel] = useState<number | null>(null);
  const [stairSteps, setStairSteps] = useState(0);
  const [saveError, setSaveError] = useState('');
  const [structureUndo, setStructureUndo] = useState<StructureUndoEntry[]>([]);
  const [pendingFloorDelete, setPendingFloorDelete] = useState<MapFloorDocument | null>(null);

  // The ortho structure viewport (MapStructureViewport) projects into the
  // shared canvas via <Viewport>, sized by PanelHost's StableSizeContent (see
  // the 'map' panel's canvasOwning: true flag in buildPanelRegistry below);
  // this route only needs the world-metre half-extent that both the
  // camera's zoom and the grid spacing are framed against - same "centred,
  // fit-to-content" convention floorExtent has always defined.
  const extent = useMemo(() => floorExtent(activeFloor), [activeFloor]);

  // Structure lives in the map document (outside the editor store's undo),
  // so changes save straight through the map API, carrying the store's live
  // instances so the write never reverts placed objects.
  async function saveStructureRaw(nextFloors: typeof mapFloors, nextStairRuns?: MapStairRun[], nextStairTriggers?: MapStairTrigger[]) {
    setSaveError('');
    const store = useMapEditorStore.getState();
    const nextMap = {
      ...project.map,
      floors: nextFloors,
      stairRuns: nextStairRuns ?? project.map.stairRuns,
      stairTriggers: nextStairTriggers ?? project.map.stairTriggers,
      objectInstances: store.objectInstances,
      lights: store.lights,
      triggers: store.triggers,
      decals: store.decals,
      markers: store.markers,
    };
    setProject({ ...project, map: nextMap });
    try {
      await saveMapDocument(projectId, nextMap);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save structure.');
    }
  }

  /** Same as `saveStructureRaw`, but records the floors/stair runs/stair
   * triggers it replaces on Map Builder's own undo stack first - structure
   * has no store-backed undo, so this is what gives draws, erases, renames
   * and new floors a Ctrl+Z path. */
  async function saveStructure(nextFloors: typeof mapFloors, label: string, nextStairRuns?: MapStairRun[], nextStairTriggers?: MapStairTrigger[]) {
    setStructureUndo((stack) =>
      [{ floors: mapFloors, stairRuns: project.map.stairRuns ?? [], stairTriggers: project.map.stairTriggers, label }, ...stack].slice(0, 20),
    );
    await saveStructureRaw(nextFloors, nextStairRuns, nextStairTriggers);
  }

  function undoStructure() {
    const [previous, ...rest] = structureUndo;
    if (!previous) return;
    setStructureUndo(rest);
    void saveStructureRaw(previous.floors, previous.stairRuns, previous.stairTriggers);
    toast(`Undo: ${previous.label}`, 'info');
  }

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.code !== 'KeyZ' || e.shiftKey || e.repeat) return;
      if (inEditableTarget()) return;
      e.preventDefault();
      undoStructure();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rebinds on every structureUndo change so the closure always sees the latest stack.
  }, [structureUndo]);

  function patchActiveFloor(
    changes: Partial<Pick<MapFloorDocument, 'walls' | 'panels' | 'areas' | 'label' | 'rooms' | 'openings' | 'voids'>>,
    label: string,
  ) {
    if (!activeFloor) return;
    void saveStructure(mapFloors.map((f) => (f.level === activeFloor.level ? { ...f, ...changes } : f)), label);
  }

  /**
   * Re-derives every room-sourced wall/panel/area on the active floor from
   * `nextRooms`/`nextOpenings`/`nextVoids` and commits the result in one
   * structure save. Always a full recompute (the same "recompute from
   * source of truth" model `buildHouse.ts` uses), not an incremental patch:
   * a room's derived walls/panel/area are identified and replaced wholesale
   * on every room-affecting edit, which means a hand-erase of a *derived*
   * wall does not persist past the next such edit - the room, not the wall,
   * is the source of truth for that geometry. Hand-drawn walls/panels (from
   * the plain 'Draw wall'/'Place panel' tools) are untagged and left alone.
   */
  function deriveFloorPatch(nextRooms: MapRoomDraft[], nextOpenings: MapOpeningDraft[], nextVoids: MapVoidDraft[]) {
    if (!activeFloor) return null;
    const derived = deriveRoomGeometry({
      floorLevel: activeFloor.level,
      rooms: nextRooms,
      openings: nextOpenings,
      voids: nextVoids,
      ceilingHeight: wallHeight,
    });
    // A derived panel/area's id is always exactly its room's id (matching
    // Dwindling's own panelFor/areaFor), so "was this previously derived" is
    // just "did the floor's room list, before this edit, contain a room
    // with this id" - hand-drawn panels/areas never collide with a room id.
    const previousRoomIds = new Set((activeFloor.rooms ?? []).map((r) => r.id));
    const handDrawnWalls = activeFloor.walls.filter((w) => !isRoomDerived(w.id));
    const handDrawnPanels = activeFloor.panels.filter((p) => !previousRoomIds.has(p.id));
    const handDrawnAreas = activeFloor.areas.filter((a) => !previousRoomIds.has(a.id));
    return {
      rooms: nextRooms,
      openings: nextOpenings,
      voids: nextVoids,
      walls: [...handDrawnWalls, ...derived.walls],
      panels: [...handDrawnPanels, ...derived.panels],
      areas: [...handDrawnAreas, ...derived.areas],
    };
  }

  function commitRoomGeometry(
    nextRooms: MapRoomDraft[],
    nextOpenings: MapOpeningDraft[],
    nextVoids: MapVoidDraft[],
    label: string,
  ) {
    const patch = deriveFloorPatch(nextRooms, nextOpenings, nextVoids);
    if (patch) patchActiveFloor(patch, label);
  }

  /** Every floor other than the active one, sorted low to high - the "to
   * floor" candidates the stairs tool's floor-pair picker offers. */
  const otherFloors = useMemo(
    () => mapFloors.filter((f) => f.level !== activeLevel).sort((a, b) => a.level - b.level),
    [mapFloors, activeLevel],
  );
  const effectiveStairToLevel =
    stairToLevel !== null && otherFloors.some((f) => f.level === stairToLevel) ? stairToLevel : (otherFloors[0]?.level ?? null);

  /**
   * A stair run or trigger between the active floor and the tool's chosen
   * "to floor" - explicitly floor-pair-aware (any other floor, not just the
   * one immediately above), unlike the reverted tool's "always the next
   * floor up" behaviour. Deliberately does not touch walls/panels/voids: a
   * `MapStairRun`'s own footprint is already its exact walkable envelope
   * (`makeWalkable`'s stair-run branch tests it directly, per
   * `mapRuntime.ts`'s doc comment), so a flight needs no accompanying void
   * to be walkable - R6 stands on its own, independent of the room tool.
   */
  function commitStair(footprint: StairFootprint) {
    if (!activeFloor || effectiveStairToLevel === null) {
      toast('Add another floor to connect stairs to first.', 'info');
      return;
    }
    const stamp = Date.now().toString(36);
    if (stairKind === 'run') {
      const run: MapStairRun = {
        id: `stair-${stamp}`,
        fromFloor: activeFloor.level,
        toFloor: effectiveStairToLevel,
        axis: footprint.axis,
        center: footprint.center,
        size: footprint.size,
        footAt: footprint.footAt,
        ...(stairSteps > 0 ? { steps: stairSteps } : {}),
      };
      void saveStructure(mapFloors, 'Place stair run', [...(project.map.stairRuns ?? []), run]);
    } else {
      const trigger: MapStairTrigger = {
        id: `stair-${stamp}`,
        center: footprint.center,
        size: footprint.size,
        fromFloor: activeFloor.level,
        toFloor: effectiveStairToLevel,
        toY: floorWorldY(effectiveStairToLevel),
        spawnXZ: footprint.center,
      };
      void saveStructure(mapFloors, 'Place stair trigger', undefined, [...project.map.stairTriggers, trigger]);
    }
  }

  function addFloor() {
    const next = createNextFloor(mapFloors);
    void saveStructure([...mapFloors, next], `Add ${next.label}`);
    setActiveLevel(next.level);
  }

  /** Deletes a floor's walls/panels plus every object, light, trigger, decal
   * and marker placed on it. Confirmed first, since it is the highest-blast-
   * radius edit this page can make. The floor's structure still lands on Map
   * Builder's own undo stack (Ctrl+Z brings the empty floor back), but the
   * purged objects go through the store's own per-id delete, a separate undo
   * stack reachable from Edit mode's Ctrl+Z - not from here - so the dialog
   * is explicit that the objects on the floor do not come back with it. */
  function confirmDeleteFloor() {
    const floor = pendingFloorDelete;
    if (!floor) return;
    setPendingFloorDelete(null);
    const store = useMapEditorStore.getState();
    const idsOnFloor = [
      ...store.objectInstances.filter((i) => i.floor === floor.level).map((i) => i.id),
      ...store.lights.filter((l) => l.floor === floor.level).map((l) => l.id),
      ...store.triggers.filter((t) => t.floor === floor.level).map((t) => t.id),
      ...store.decals.filter((d) => d.floor === floor.level).map((d) => d.id),
      ...store.markers.filter((m) => m.floor === floor.level).map((m) => m.id),
    ];
    for (const id of idsOnFloor) store.deleteObject(id);
    const nextFloors = removeFloor(mapFloors, floor.level);
    // A stair run/trigger linking to the deleted floor from either end would
    // otherwise dangle, referencing a `fromFloor`/`toFloor` that no longer
    // exists.
    const nextStairRuns = (project.map.stairRuns ?? []).filter((r) => r.fromFloor !== floor.level && r.toFloor !== floor.level);
    const nextStairTriggers = project.map.stairTriggers.filter((t) => t.fromFloor !== floor.level && t.toFloor !== floor.level);
    void saveStructure(nextFloors, `Delete ${floor.label}`, nextStairRuns, nextStairTriggers);
    if (activeLevel === floor.level) setActiveLevel(nextFloors[0]?.level ?? 0);
  }

  function commitDrag(current: BlueprintDrag) {
    if (!activeFloor) return;
    // commitDrag only ever runs from the canvas's onPointerUp handler (see the
    // blueprint viewport below), never during render; the id stamp just needs
    // to be unique per drag.
    // eslint-disable-next-line react-hooks/purity -- see comment above
    const stamp = Date.now().toString(36);
    if (tool === 'Draw wall') {
      const wall = wallFromDrag(current.from, current.to, {
        id: `wall-${stamp}`,
        snap: gridSnap,
        height: wallHeight,
        thickness: thicknessCm / 100,
        color: '#4a525c',
        freeAngle: current.freeAngle,
      });
      if (wall) patchActiveFloor({ walls: [...activeFloor.walls, wall] }, 'Draw wall');
    } else if (tool === 'Place panel') {
      const panel = panelFromDrag(current.from, current.to, {
        id: `panel-${stamp}`,
        snap: gridSnap,
        ceilingHeight: wallHeight,
        color: '#3a4048',
      });
      if (panel) patchActiveFloor({ panels: [...activeFloor.panels, panel] }, 'Place panel');
    } else if (tool === 'Draw room') {
      // Reuse panelFromDrag purely for its snap-to-grid rectangle math, so a
      // room footprint snaps identically to a hand-placed panel.
      const footprint = panelFromDrag(current.from, current.to, {
        id: 'footprint',
        snap: gridSnap,
        ceilingHeight: wallHeight,
        color: '#3a4048',
      });
      if (!footprint) {
        // Too small to be a new room - treat as a click and select whatever
        // room (if any) is under the pointer instead.
        const point = current.to;
        const hitRoom = (activeFloor.rooms ?? []).find((r) => point[0] >= r.x[0] && point[0] <= r.x[1] && point[1] >= r.z[0] && point[1] <= r.z[1]);
        setSelectedRoomId(hitRoom?.id ?? null);
        return;
      }
      const rooms = activeFloor.rooms ?? [];
      const room: MapRoomDraft = {
        id: `room-${stamp}`,
        name: `Room ${rooms.length + 1}`,
        x: [round1(footprint.center[0] - footprint.size[0] / 2), round1(footprint.center[0] + footprint.size[0] / 2)],
        z: [round1(footprint.center[1] - footprint.size[1] / 2), round1(footprint.center[1] + footprint.size[1] / 2)],
      };
      commitRoomGeometry([...rooms, room], activeFloor.openings ?? [], activeFloor.voids ?? [], 'Draw room');
      setSelectedRoomId(room.id);
    } else if (tool === 'Place stairs') {
      const footprint = stairFootprintFromDrag(current.from, current.to, { snap: gridSnap });
      if (footprint) commitStair(footprint);
    }
  }

  function round1(value: number): number {
    return Math.round(value * 10) / 10;
  }

  /** Deletes a room and re-derives the floor without it - called from the
   * room inspector's own delete button, not the generic 'Erase' tool, so a
   * room is never lost to a stray click while drawing. */
  function deleteRoom(room: MapRoomDraft) {
    if (!activeFloor) return;
    const nextRooms = (activeFloor.rooms ?? []).filter((r) => r.id !== room.id);
    commitRoomGeometry(nextRooms, activeFloor.openings ?? [], activeFloor.voids ?? [], `Delete ${room.name}`);
    if (selectedRoomId === room.id) setSelectedRoomId(null);
    toast(`Removed room "${room.name}". Ctrl+Z to undo.`, 'info');
  }

  /** Deletes a stair run/trigger - called from the stairs tool's own list,
   * not the generic 'Erase' tool, matching how a room is deleted. */
  function deleteStairRun(run: MapStairRun) {
    void saveStructure(mapFloors, 'Delete stair run', (project.map.stairRuns ?? []).filter((r) => r.id !== run.id));
    toast('Removed stair run. Ctrl+Z to undo.', 'info');
  }
  function deleteStairTrigger(trigger: MapStairTrigger) {
    void saveStructure(mapFloors, 'Delete stair trigger', undefined, project.map.stairTriggers.filter((t) => t.id !== trigger.id));
    toast('Removed stair trigger. Ctrl+Z to undo.', 'info');
  }

  function eraseAt(point: [number, number]) {
    if (!activeFloor) return;
    const hit = hitTestStructure(point, activeFloor);
    if (!hit) return;
    if (hit.kind === 'wall') {
      if (isRoomDerived(hit.id)) {
        toast('That wall belongs to a room and will come back on the next room edit. Erase or resize the room instead.', 'info');
      }
      patchActiveFloor({ walls: activeFloor.walls.filter((w) => w.id !== hit.id) }, 'Erase wall');
      toast('Removed wall. Ctrl+Z to undo.', 'info');
    } else {
      patchActiveFloor({ panels: activeFloor.panels.filter((p) => p.id !== hit.id) }, 'Erase panel');
      toast('Removed panel. Ctrl+Z to undo.', 'info');
    }
  }

  const floors = useMemo(
    () =>
      mapFloors.map((f) => ({
        level: f.level,
        name: f.label,
        meta: `${f.panels.length} panel${f.panels.length === 1 ? '' : 's'} · ${f.walls.length} wall${f.walls.length === 1 ? '' : 's'} · ${
          instances.filter((i) => i.floor === f.level).length
        } object${instances.filter((i) => i.floor === f.level).length === 1 ? '' : 's'}`,
      })),
    [mapFloors, instances],
  );
  const totals = useMemo(() => {
    const assetIds = new Set(instances.map((i) => i.assetId));
    const uploadIds = new Set(project.assets.map((a) => a.id));
    const uploadsUsed = new Set(instances.filter((i) => uploadIds.has(i.assetId)).map((i) => i.assetId));
    return [
      [String(instances.length), 'Objects'],
      [String(assetIds.size), 'Distinct assets'],
      [String(uploadsUsed.size), 'Uploads used'],
      [String(project.characters.length), 'Characters'],
    ] as [string, string][];
  }, [instances, project.assets, project.characters]);
  // Structure-drawing tools. These live in the viewport bar above the
  // canvas, not in a side panel: a tool palette is a viewport control in
  // every reference editor (Godot's 2D/3D toolbars, Unreal's viewport
  // toolbar transform group, LDtk's tool bar), and keeping them there frees
  // the right column to be what Edit's right column already is - the
  // properties of whatever is selected.
  const tools: [string, ReactNode][] = [
    ['Draw wall', <svg key="w" width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M2 11.5h12" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" /><path d="M2 6.5h8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" opacity="0.5" /></svg>],
    ['Place panel', <svg key="p" width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true"><rect x="2.5" y="4.5" width="11" height="7" rx="1" stroke="currentColor" strokeWidth="1.3" /></svg>],
    ['Draw room', <svg key="r" width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true"><rect x="2.5" y="2.5" width="11" height="11" rx="1" stroke="currentColor" strokeWidth="1.3" /><path d="M2.5 10h3" stroke="currentColor" strokeWidth="1.3" /></svg>],
    ['Place stairs', <svg key="s" width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M2 13h3.5V9.5H9V6h3.5V2.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" /></svg>],
    ['Erase', <svg key="e" width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3 12.5h10" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" /><path d="M5.5 10.5l5-5 2.5 2.5-5 5H5.5v-2.5z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" /></svg>],
  ];

  // Live drag preview as a snapped wall, panel, room, or stair footprint.
  const preview = useMemo<MapStructurePreview | null>(() => {
    if (!drag) return null;
    if (tool === 'Draw wall') {
      const wall = wallFromDrag(drag.from, drag.to, { id: 'preview', snap: gridSnap, height: wallHeight, thickness: thicknessCm / 100, freeAngle: drag.freeAngle });
      return wall ? { kind: 'wall' as const, wall } : null;
    }
    if (tool === 'Place panel' || tool === 'Draw room') {
      const color = tool === 'Draw room' ? '#6b5bd6' : '#3a4048';
      const panel = panelFromDrag(drag.from, drag.to, { id: 'preview', snap: gridSnap, ceilingHeight: wallHeight, color });
      const kind: 'room' | 'panel' = tool === 'Draw room' ? 'room' : 'panel';
      return panel ? { kind, panel } : null;
    }
    if (tool === 'Place stairs') {
      const footprint = stairFootprintFromDrag(drag.from, drag.to, { snap: gridSnap });
      return footprint ? { kind: 'stairs' as const, footprint } : null;
    }
    return null;
  }, [drag, tool, gridSnap, wallHeight, thicknessCm]);

  return (
    // See EditRoute's own note on `minWidth: 0` - same reason, same shape.
    <div style={{ flex: 1, display: 'flex', minWidth: 0, minHeight: 0 }}>
      <M2Panel side="left">
        <M2Label action={
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="ss-btn ss-btn--ghost" disabled={structureUndo.length === 0} title={structureUndo.length > 0 ? `Undo: ${structureUndo[0]!.label} (Ctrl+Z)` : 'Nothing to undo'} style={{ padding: '3px 10px', fontSize: 11, borderRadius: 7, opacity: structureUndo.length === 0 ? 0.45 : 1 }} onClick={undoStructure}>Undo</button>
            <button className="ss-btn ss-btn--ghost" style={{ padding: '3px 10px', fontSize: 11, borderRadius: 7 }} onClick={addFloor}>+ Floor</button>
          </div>
        }>Floors</M2Label>
        {floors.length === 0 && (
          <div style={{ padding: '0 16px', fontSize: 12, color: 'var(--ss-text-3)', lineHeight: 1.55 }}>
            No floors yet. Add one to start drawing structure.
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '0 8px' }}>
          {floors.map((f) => (
            <M2ListRow
              key={f.level}
              active={activeLevel === f.level}
              onClick={() => setActiveLevel(f.level)}
              truncate={false}
              labelStyle={{ display: 'flex', flexDirection: 'column', gap: 3, padding: '9px 12px' }}
              onDelete={() => {
                const full = mapFloors.find((mf) => mf.level === f.level);
                if (full) setPendingFloorDelete(full);
              }}
              deleteLabel={`Delete ${f.name}`}
            >
              <span style={{ fontSize: 'var(--ss-fs-sm)', fontWeight: 600, color: activeLevel === f.level ? 'var(--ss-accent)' : 'var(--ss-text)' }}>{f.name}</span>
              <span style={{ fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)' }}>{f.meta}</span>
            </M2ListRow>
          ))}
        </div>
        <M2Label>Scene totals</M2Label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, padding: '0 16px' }}>
          {totals.map(([n, l]) => (
            <div key={l} style={{ border: '1px solid var(--ss-border-soft)', borderRadius: 10, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ fontFamily: 'var(--ss-display)', fontSize: 'var(--ss-fs-xl)', lineHeight: 1.1 }}>{n}</span>
              <span style={{ fontSize: 'var(--ss-fs-2xs)', color: 'var(--ss-text-3)' }}>{l}</span>
            </div>
          ))}
        </div>
      </M2Panel>
      {/* ortho structure-editing viewport - a real top-down camera over the
          shared LevelRenderer scene (see MapStructureViewport in
          engine/runtime/LevelRenderer.tsx), replacing the old bespoke
          SVG/DOM blueprint editor. Tool state and the drag/commit/erase
          pipeline above are unchanged; only how pointer events become world
          coordinates (ground-plane raycasting instead of worldFromCanvas)
          and how structure renders (real 3D meshes instead of absolutely
          positioned divs) moved. */}
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, minHeight: 0 }}>
        <ViewportBar sections={[
          {
            id: 'tools',
            pinned: true,
            node: tools.map(([name, icon]) => (
              <VtToolButton key={name} active={tool === name} label={name} onClick={() => setTool(name)}>{icon}</VtToolButton>
            )),
          },
          {
            id: 'floor',
            node: <VtChip on label={(activeFloor?.label ?? 'No floor').toUpperCase()} hint="The floor being drawn on. Switch floors in the Floors list to the left." />,
          },
          {
            id: 'snap',
            node: <VtChip label={`GRID ${gridSnap.toFixed(2)}M`} hint="Drawing snaps to this grid. Change it under Floor settings." />,
          },
          {
            id: 'view',
            node: <VtChip on label={is3d ? '3D' : '2D'} hint="Game dimension. Set at creation, changeable in Game Setup." />,
          },
        ]} />
        <div style={{ position: 'relative', flex: 1, minHeight: 0, minWidth: 0, overflow: 'hidden', background: '#060607', cursor: 'crosshair', touchAction: 'none' }}>
          <MapStructureViewport
            mapDocument={project.map}
            activeLevel={activeLevel}
            extent={extent}
            gridSnap={gridSnap}
            selectedRoomId={selectedRoomId}
            stairKind={stairKind}
            preview={preview}
            onPointerDownWorld={(point, shiftKey) => {
              if (!activeFloor) return;
              if (tool === 'Erase') {
                eraseAt(point);
                return;
              }
              setDrag({ from: point, to: point, freeAngle: shiftKey });
            }}
            onPointerMoveWorld={(point, shiftKey) => {
              if (!drag) return;
              setDrag({ from: drag.from, to: point, freeAngle: shiftKey });
            }}
            onPointerUpWorld={() => {
              if (drag) commitDrag(drag);
              setDrag(null);
            }}
          />
          <ViewportChip style={{ top: 14, left: 14 }}>{(activeFloor?.label ?? 'no floor').toLowerCase().replace(/ /g, '_')} · {is3d ? '3d blockout' : '2d plan'} · ±{extent.toFixed(0)} m</ViewportChip>
          {saveError && (
            <ViewportChip style={{ top: 14, right: 14, color: 'var(--ss-danger, #e06060)' }}>{saveError}</ViewportChip>
          )}
          <ViewportChip style={{ bottom: 14, left: '50%', transform: 'translateX(-50%)' }}>
            {tool === 'Erase'
              ? 'Click a wall or panel to remove it · Ctrl+Z to undo'
              : tool === 'Draw wall'
                ? 'Click-drag to draw · Snaps to the grid · Hold Shift for an angled wall'
                : tool === 'Draw room'
                  ? 'Click-drag to draw a room · Rooms sharing an edge get one merged wall · Click a room to edit it'
                  : tool === 'Place stairs'
                    ? `Click-drag to draw a flight or trigger to floor ${effectiveStairToLevel ?? '-'}`
                    : 'Click-drag to draw · Snaps to the grid'}
          </ViewportChip>
        </div>
      </div>
      <M2Panel side="right">
        {tool === 'Draw room' && activeFloor && (() => {
          const rooms = activeFloor.rooms ?? [];
          const selected = rooms.find((r) => r.id === selectedRoomId) ?? null;
          function updateRoom(patch: Partial<MapRoomDraft>) {
            if (!selected) return;
            const nextRooms = rooms.map((r) => (r.id === selected.id ? { ...r, ...patch } : r));
            commitRoomGeometry(nextRooms, activeFloor!.openings ?? [], activeFloor!.voids ?? [], `Edit ${selected.name}`);
          }
          return (
            <Fragment>
              <M2Label>{selected ? 'Room' : 'Rooms'}</M2Label>
              {selected ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '0 16px' }}>
                  <EdNumber label="Name" value={selected.name} onCommit={(raw) => { const name = raw.trim(); if (name) updateRoom({ name }); }} />
                  <div style={{ fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)' }}>
                    {(selected.x[1] - selected.x[0]).toFixed(1)} x {(selected.z[1] - selected.z[0]).toFixed(1)} m
                  </div>
                  <button
                    className="ss-btn ss-btn--ghost"
                    onClick={() => updateRoom({ openAbove: !selected.openAbove })}
                    style={{ padding: '7px 10px', fontSize: 'var(--ss-fs-xs)', borderRadius: 7, textAlign: 'left' }}
                    title="Open above leaves no ceiling on this room, for a stairwell or void a flight rises through"
                  >
                    {selected.openAbove ? '✓ ' : ''}Open above
                  </button>
                  <button
                    className="ss-btn ss-btn--ghost"
                    onClick={() => deleteRoom(selected)}
                    style={{ padding: '7px 10px', fontSize: 'var(--ss-fs-xs)', borderRadius: 7, textAlign: 'left', color: 'var(--ss-danger)' }}
                  >Delete room</button>
                </div>
              ) : rooms.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '0 8px' }}>
                  {rooms.map((r) => (
                    <M2ListRow key={r.id} onClick={() => setSelectedRoomId(r.id)} title={`Edit ${r.name}`}>{r.name}</M2ListRow>
                  ))}
                </div>
              ) : (
                <div style={{ padding: '0 16px', fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)', lineHeight: 1.55 }}>
                  Click-drag on the canvas to draw a room. Rooms sharing an edge merge into one wall automatically, with a doorway wherever you add one.
                </div>
              )}
            </Fragment>
          );
        })()}
        {tool === 'Place stairs' && activeFloor && (() => {
          const runsHere = (project.map.stairRuns ?? []).filter((r) => r.fromFloor === activeFloor.level);
          const triggersHere = project.map.stairTriggers.filter((t) => t.fromFloor === activeFloor.level);
          return (
            <Fragment>
              <M2Label>Stairs</M2Label>
              <div style={{ display: 'flex', gap: 5, padding: '0 16px', marginBottom: 10 }}>
                {(['run', 'trigger'] as const).map((kind) => (
                  <button key={kind} onClick={() => setStairKind(kind)} style={{
                    flex: 1, padding: '7px 10px', fontSize: 'var(--ss-fs-xs)', textAlign: 'center', borderRadius: 7,
                    border: '1px solid ' + (stairKind === kind ? 'var(--ss-accent-border)' : 'var(--ss-border-soft)'),
                    background: stairKind === kind ? 'var(--ss-accent-soft)' : 'transparent',
                    color: stairKind === kind ? 'var(--ss-accent)' : 'var(--ss-text-2)', fontWeight: 500,
                  }}>{kind === 'run' ? 'Walkable run' : 'Instant trigger'}</button>
                ))}
              </div>
              {otherFloors.length === 0 ? (
                <div style={{ padding: '0 16px', fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)', lineHeight: 1.55 }}>
                  Add another floor before placing stairs - a flight needs somewhere to climb to.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '0 16px', marginBottom: 8 }}>
                  <EdSelect label="To floor" value={String(effectiveStairToLevel ?? '')} onChange={(value) => setStairToLevel(Number(value))}>
                    {otherFloors.map((f) => (
                      <option key={f.level} value={f.level}>{f.label}</option>
                    ))}
                  </EdSelect>
                  {stairKind === 'run' && (
                    <EdNumber
                      label="Steps (0 = smooth ramp)"
                      value={String(stairSteps)}
                      onCommit={(raw) => { const v = Math.round(Number(raw)); if (Number.isFinite(v) && v >= 0) setStairSteps(v); }}
                    />
                  )}
                </div>
              )}
              {(runsHere.length > 0 || triggersHere.length > 0) && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '0 12px' }}>
                  {runsHere.map((r) => (
                    <div key={r.id} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                      padding: '6px 10px', fontSize: 'var(--ss-fs-sm)', borderRadius: 7, border: '1px solid var(--ss-border-soft)', color: 'var(--ss-text-2)',
                    }}>
                      <span>Run to floor {r.toFloor}</span>
                      <button
                        onClick={() => deleteStairRun(r)}
                        title="Delete this stair run"
                        aria-label="Delete this stair run"
                        style={{ border: 'none', background: 'transparent', padding: '2px 6px', fontSize: 13, lineHeight: 1, color: 'var(--ss-text-3)' }}
                      >×</button>
                    </div>
                  ))}
                  {triggersHere.map((t) => (
                    <div key={t.id} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                      padding: '6px 10px', fontSize: 'var(--ss-fs-sm)', borderRadius: 7, border: '1px solid var(--ss-border-soft)', color: 'var(--ss-text-2)',
                    }}>
                      <span>Trigger to floor {t.toFloor}</span>
                      <button
                        onClick={() => deleteStairTrigger(t)}
                        title="Delete this stair trigger"
                        aria-label="Delete this stair trigger"
                        style={{ border: 'none', background: 'transparent', padding: '2px 6px', fontSize: 13, lineHeight: 1, color: 'var(--ss-text-3)' }}
                      >×</button>
                    </div>
                  ))}
                </div>
              )}
            </Fragment>
          );
        })()}
        <M2Label>Floor settings</M2Label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '0 16px' }}>
          <EdNumber
            label="Floor name"
            value={activeFloor?.label ?? ''}
            disabled={!activeFloor}
            onCommit={(raw) => { const label = raw.trim(); if (label) patchActiveFloor({ label }, 'Rename floor'); }}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <EdNumber label="Height" value={wallHeight.toFixed(1)} onCommit={(raw) => { const v = Number(raw); if (Number.isFinite(v) && v > 0) setWallHeight(v); }}
              muted={!is3d} title={!is3d ? '3D wall height - cosmetic only for 2D, does not drive 2D collision' : undefined} />
            <EdNumber label="Grid snap" value={gridSnap.toFixed(2)} onCommit={(raw) => { const v = Number(raw); if (Number.isFinite(v) && v > 0) setGridSnap(v); }} />
          </div>
        </div>
        <EdSlider label="Wall thickness" suffix="cm" value={thicknessCm} onChange={setThicknessCm}
          hint={!is3d ? '3D wall thickness - cosmetic only for 2D, does not drive 2D collision' : undefined} />
        <div style={{ marginTop: 'auto', padding: '18px 16px 4px', fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)', lineHeight: 1.55 }}>
          {is3d ? (
            'Structure drawn here becomes collidable 3D geometry in Edit mode automatically.'
          ) : (
            <Fragment>
              Structure drawn here is decorative only: 2D collision stays tilemap driven, paint solid tiles in{' '}
              <Link to={`${editorBase}/pixel`} style={{ color: 'var(--ss-accent)' }}>Pixel Studio</Link>.
            </Fragment>
          )}
        </div>
      </M2Panel>
      {pendingFloorDelete && (
        <EdConfirmDialog
          title={`Delete "${pendingFloorDelete.label}"?`}
          body={`This permanently removes every wall and panel on ${pendingFloorDelete.label}, and deletes every object, light, trigger, decal and spawn marker placed on it. The floor itself is undoable with Ctrl+Z afterwards, but those objects are not - this cannot be undone from Map Builder.`}
          confirmLabel="Delete floor"
          onConfirm={confirmDeleteFloor}
          onCancel={() => setPendingFloorDelete(null)}
        />
      )}
    </div>
  );
}

/* ── Animation Studio: render assets, browse clips, pose pre-rigged ────── */
export interface AnimModel {
  id: string;
  kind: 'RIGGED' | 'PROP' | 'STATIC';
  note: string;
  /** Set for a project's own registered character/rig (not the built-in biped). */
  custom?: CharacterDefinition;
  /** Set for a project's own uploaded GLB asset (kind: 'STATIC'): its
   * download URL, so the viewer can actually render it. */
  assetUrl?: string;
}

export const ANIM_MODELS: AnimModel[] = [
  { id: 'neutral_biped', kind: 'RIGGED', note: '53 joints · 45 clips' },
  { id: 'ual2_standard', kind: 'RIGGED', note: 'UAL2 Standard · 65 joints · 45 clips (retargeted)' },
  { id: 'ual2_mannequin_f', kind: 'RIGGED', note: 'UAL2 Mannequin F · 65 joints · 45 clips (retargeted)' },
];

/** Built-in rig ids (ANIM_MODELS entries with no `custom`) that load a rig
 * other than the canonical Animated Base Character - each maps to a
 * PrototypeGltf loader passed as AnimationGalleryStage's `loader` prop. A
 * model id not listed here (just 'neutral_biped' today) uses the stage's own
 * loadPrototypeGltf default. */
const BUILT_IN_RIG_LOADERS: Record<string, () => Promise<PrototypeGltf>> = {
  ual2_standard: loadUal2StandardPrototypeGltf,
  ual2_mannequin_f: loadUal2MannequinFPrototypeGltf,
};

/** The engine's clip groups in the design's display shape. */
const CLIP_GROUP_LABELS: Record<string, string> = {
  locomotion: 'Locomotion',
  traversal: 'Traversal',
  combatPistol: 'Combat · Pistol',
  combatMelee: 'Combat · Melee',
  combatMagic: 'Combat · Magic',
  reactions: 'Reactions',
  social: 'Social',
  tasks: 'Tasks',
  vehicle: 'Vehicle',
  swim: 'Swim',
};
export const ANIM_CLIP_GROUPS: [string, string[]][] = Object.entries(CLIP_GROUPS).map(([group, clips]) => [
  CLIP_GROUP_LABELS[group] ?? group,
  [...clips],
]);

/** Builds the Animation Studio's left-panel model list: the stock rigged
 * biped, any project-registered custom rigs, and the project's own uploaded
 * GLB assets - `kind: 'texture' | 'audio'` assets never show up here, only
 * real GLBs get a model row. Pure so the filtering is unit-testable without
 * mounting the route. */
export function buildAnimationModels(customCharacters: CharacterDefinition[], assets: StudioAssetRecord[]): AnimModel[] {
  return [
    ...ANIM_MODELS,
    ...customCharacters.map((c) => {
      const bones = getRigBones(c.rigId) ?? CORE_RIG_BONES[c.rigId] ?? [];
      return { id: c.id, kind: 'RIGGED' as const, note: `Custom rig · ${bones.length} bones`, custom: c };
    }),
    ...assets
      .filter((a) => a.kind === 'glb')
      .map((a) => ({ id: a.name, kind: 'STATIC' as const, note: 'No rig yet', assetUrl: a.downloadUrl })),
  ];
}

/** Model-list rows eligible to attach as a hand prop - a project's own
 * uploaded GLB assets only (`kind: 'STATIC'` with a resolved `assetUrl`);
 * the stock biped and custom rigs never qualify as props themselves. */
export function filterAttachableAssets(models: AnimModel[]): AnimModel[] {
  return models.filter((m) => m.kind === 'STATIC' && m.assetUrl != null);
}

export function AnimationRoute() {
  const { project } = useEditorHost();
  const { reload } = useProject();
  const customCharacters = useMemo(
    () => project.characters.filter((c) => c.id !== NEUTRAL_BIPED_DEFINITION.id),
    [project.characters],
  );
  const models = useMemo<AnimModel[]>(
    () => buildAnimationModels(customCharacters, project.assets),
    [customCharacters, project.assets],
  );
  const [model, setModel] = useState('neutral_biped');
  const [clip, setClip] = useState('Idle_Loop');
  const [playing, setPlaying] = useState(true);
  const [speedPct, setSpeedPct] = useState(50);
  const [weapon, setWeapon] = useState('None');
  // ── Custom rigging: import + retarget (SF22) ──
  const [mappingTarget, setMappingTarget] = useState<{ id: string; url: string } | null>(null);
  const [mappingStatus, setMappingStatus] = useState<string | null>(null);
  const saveBoneMapping = useCallback(
    async (mapping: BoneMapping, sourceJointNames: string[]) => {
      if (!mappingTarget) return;
      const rigId = `imported-${mappingTarget.id}`;
      const definition: CharacterDefinition = {
        id: `char-${mappingTarget.id}`,
        name: mappingTarget.id,
        kind: 'npc',
        rigId,
        rendererId: `imported-skeleton:${rigId}`,
        importedGlbUrl: mappingTarget.url,
        boneMapping: mapping,
      };
      const otherCharacters = project.characters.filter(
        (c) => c.id !== NEUTRAL_BIPED_DEFINITION.id && c.id !== definition.id,
      );
      const nextRigBones = { ...project.rigBones, [rigId]: sourceJointNames };
      try {
        setMappingStatus('Saving…');
        await saveCharacterRoster(project.meta.id, [...otherCharacters, definition], nextRigBones);
        await reload();
        setMappingTarget(null);
        setMappingStatus(null);
        setModel(definition.id);
        setLibModel(null);
      } catch (err) {
        setMappingStatus(err instanceof Error ? err.message : 'Failed to save the bone mapping.');
      }
    },
    [mappingTarget, project, reload],
  );
  // ── Auto-rigging: landmark markers -> canonical skeleton (3.3) ──
  // Unlike saveBoneMapping above, this reuses NEUTRAL_BIPED_RIG_ID directly
  // (no boneMapping): the built skeleton's own bone names already match
  // CORE_RIG_BONES exactly, so posing/pose-library support just works via
  // the existing rendererId -> createImportedSkeletonRenderer wiring.
  const [autoRigTarget, setAutoRigTarget] = useState<{ id: string; url: string } | null>(null);
  const [autoRigStatus, setAutoRigStatus] = useState<string | null>(null);
  const saveAutoRig = useCallback(
    async (built: { scene: Group; mesh: SkinnedMesh }) => {
      if (!autoRigTarget) return;
      try {
        setAutoRigStatus('Exporting…');
        const exporter = new GLTFExporter();
        const result = await exporter.parseAsync(built.scene, { binary: true });
        if (!(result instanceof ArrayBuffer)) throw new Error('Auto-rig export did not produce binary output.');
        setAutoRigStatus('Uploading…');
        const file = new File([result], `${autoRigTarget.id}-autorig.glb`, { type: 'model/gltf-binary' });
        const asset = await uploadGlbAsset(project.meta.id, file);
        const definition: CharacterDefinition = {
          id: `char-autorig-${autoRigTarget.id}`,
          name: `${autoRigTarget.id} (auto-rigged)`,
          kind: 'npc',
          rigId: NEUTRAL_BIPED_RIG_ID,
          // Keyed on the freshly-uploaded asset's own id, not autoRigTarget.id
          // (the SOURCE model, constant across retries) - registerProjectCharacters
          // only ever registers a renderer once per rendererId and never
          // replaces it, so reusing the same id across a retry would leave
          // the character permanently rendering the FIRST attempt's mesh no
          // matter how many times the creator re-runs auto-rig on it.
          rendererId: `imported-skeleton:autorig-${asset.id}`,
          importedGlbUrl: asset.downloadUrl,
        };
        const otherCharacters = project.characters.filter(
          (c) => c.id !== NEUTRAL_BIPED_DEFINITION.id && c.id !== definition.id,
        );
        setAutoRigStatus('Saving…');
        await saveCharacterRoster(project.meta.id, [...otherCharacters, definition], project.rigBones);
        await reload();
        setAutoRigTarget(null);
        setAutoRigStatus(null);
        setModel(definition.id);
        setLibModel(null);
      } catch (err) {
        setAutoRigStatus(err instanceof Error ? err.message : 'Failed to build the auto-rig.');
      }
    },
    [autoRigTarget, project, reload],
  );
  // Shared-library animated models: their clips play on their own model
  // (no retargeting); the prototype-rig flow stays exactly as it was.
  const registryVersion = useSyncExternalStore(subscribeObjectRegistry, getObjectRegistryVersion);
  const [libSearch, setLibSearch] = useState('');
  const [libModel, setLibModel] = useState<LibraryAnimatedModel | null>(null);
  const libraryModels = useMemo(() => {
    void registryVersion;
    const q = libSearch.trim().toLowerCase();
    const all = listLibraryAnimatedModels();
    const matches = q
      ? all.filter((x) => x.name.toLowerCase().includes(q) || x.bundle.toLowerCase().includes(q) || x.author.toLowerCase().includes(q))
      : all;
    const shown = matches.slice(0, 120);
    const byAuthor = new Map<string, Map<string, LibraryAnimatedModel[]>>();
    for (const m of shown) {
      if (!byAuthor.has(m.author)) byAuthor.set(m.author, new Map());
      const bb = byAuthor.get(m.author)!;
      if (!bb.has(m.bundle)) bb.set(m.bundle, []);
      bb.get(m.bundle)!.push(m);
    }
    return { byAuthor, total: all.length, matchTotal: matches.length, shownCount: shown.length };
  }, [libSearch, registryVersion]);
  const m = models.find((x) => x.id === model) ?? ANIM_MODELS[0]!;
  const rigged = !libModel && m.kind === 'RIGGED';
  const galleryClip: PrototypeClip = isPrototypeClip(clip) ? clip : 'Idle_Loop';
  // A retargeted custom rig (SF22: an imported GLB with a saved bone mapping)
  // plays the shared 45-clip set through RetargetedGalleryStage, the same
  // clip-transport UI as the stock biped - not the flat bone-transform
  // CustomRigStage posing flow, even though it is also a `m.custom` entry.
  const isRetargeted = !libModel && !!m.custom?.importedGlbUrl && !!m.custom.boneMapping;
  const selectedCustom = !libModel && !isRetargeted ? m.custom : undefined;

  // ── Prop attachment (stock rigged biped only - AnimationGalleryStage is the
  // only stage wired to PrototypeInstance.attachToHand; custom rigs, retargeted
  // or not, have no attach point yet). ──
  const canAttachProp = rigged && !selectedCustom && !isRetargeted;
  const attachableAssets = useMemo(() => filterAttachableAssets(models), [models]);
  const selectedAttachment = attachableAssets.find((x) => x.id === weapon);
  const galleryProp = useMemo(
    () => (canAttachProp && selectedAttachment ? { url: selectedAttachment.assetUrl! } : null),
    [canAttachProp, selectedAttachment],
  );

  // Custom-rig posing (project-registered characters, not the neutral biped)
  // is handled by mounting the shared `CharacterStudio` component below once
  // one is selected - it owns its own pose loading/saving via the same
  // `getPosePersistence()`/`getActiveProjectId()` this route used to call
  // directly, so none of that state needs to live here.
  const customBones = useMemo(
    () => (selectedCustom ? getRigBones(selectedCustom.rigId) ?? CORE_RIG_BONES[selectedCustom.rigId] ?? [] : []),
    [selectedCustom],
  );

  if (mappingTarget) {
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderBottom: '1px solid var(--ss-border-soft)', flexShrink: 0 }}>
          <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ss-text-3)' }}>
            Map bones · {mappingTarget.id}
          </span>
          {mappingStatus && <span style={{ fontSize: 11.5, color: 'var(--ss-text-3)' }}>{mappingStatus}</span>}
        </div>
        <BoneMappingPanel
          sourceUrl={mappingTarget.url}
          onCancel={() => { setMappingTarget(null); setMappingStatus(null); }}
          onSave={(mapping, sourceJointNames) => void saveBoneMapping(mapping, sourceJointNames)}
        />
      </div>
    );
  }

  if (autoRigTarget) {
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderBottom: '1px solid var(--ss-border-soft)', flexShrink: 0 }}>
          <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ss-text-3)' }}>
            Auto-rig · {autoRigTarget.id}
          </span>
          {autoRigStatus && <span style={{ fontSize: 11.5, color: 'var(--ss-text-3)' }}>{autoRigStatus}</span>}
        </div>
        <AutoRigPanel
          sourceUrl={autoRigTarget.url}
          onCancel={() => { setAutoRigTarget(null); setAutoRigStatus(null); }}
          onBuilt={(built) => void saveAutoRig(built)}
        />
      </div>
    );
  }

  // Posing a project-registered custom rig: hand off to the shared
  // CharacterStudio rather than reimplementing bone sliders and pose
  // save/load here. Keeps the model switcher on the left so leaving posing
  // mode is just picking something else; CharacterStudio owns everything to
  // its right, including its own viewport, lighting and pose persistence.
  if (selectedCustom) {
    return (
      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <M2Panel side="left" width={264}>
          <M2Label>Model</M2Label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '0 8px' }}>
            {models.map((x) => (
              <div key={x.id} style={{ display: 'flex', alignItems: 'stretch', gap: 4 }}>
                <button onClick={() => { setModel(x.id); setLibModel(null); setClip('Idle_Loop'); }} style={{
                  display: 'flex', alignItems: 'center', gap: 9, padding: '8px 12px', borderRadius: 9, textAlign: 'left', flex: 1, minWidth: 0,
                  border: '1px solid ' + (!libModel && model === x.id ? 'var(--ss-accent-border)' : 'transparent'),
                  background: !libModel && model === x.id ? 'var(--ss-accent-soft)' : 'transparent',
                  color: !libModel && model === x.id ? 'var(--ss-accent)' : 'var(--ss-text-2)', fontSize: 12.5, fontWeight: 500,
                }}>
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{x.id}</span>
                  <span style={{ fontSize: 8.5, fontWeight: 600, letterSpacing: '0.12em', color: x.kind === 'RIGGED' ? 'var(--ss-success)' : 'var(--ss-text-3)', border: '1px solid var(--ss-border-soft)', borderRadius: 999, padding: '2px 7px', flexShrink: 0 }}>{x.kind}</span>
                </button>
                {x.kind === 'STATIC' && x.assetUrl && (
                  <button
                    type="button"
                    title="Map this GLB's skeleton onto the shared 45-clip set"
                    onClick={() => setMappingTarget({ id: x.id, url: x.assetUrl! })}
                    style={{ flexShrink: 0, padding: '0 9px', fontSize: 9.5, fontWeight: 600, letterSpacing: '0.06em', borderRadius: 7, border: '1px solid var(--ss-border-soft)', background: 'transparent', color: 'var(--ss-text-3)' }}
                  >
                    Rig…
                  </button>
                )}
                {x.kind === 'STATIC' && x.assetUrl && (
                  <button
                    type="button"
                    title="Place landmark markers to auto-rig this mesh onto the canonical skeleton"
                    onClick={() => setAutoRigTarget({ id: x.id, url: x.assetUrl! })}
                    style={{ flexShrink: 0, padding: '0 9px', fontSize: 9.5, fontWeight: 600, letterSpacing: '0.06em', borderRadius: 7, border: '1px solid var(--ss-border-soft)', background: 'transparent', color: 'var(--ss-text-3)' }}
                  >
                    Auto-rig…
                  </button>
                )}
              </div>
            ))}
          </div>
        </M2Panel>
        <CharacterStudio
          characters={[selectedCustom]}
          rigBones={{ [selectedCustom.rigId]: customBones }}
          style={{ flex: 1 }}
        />
      </div>
    );
  }

  return (
    <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
      <M2Panel side="left" width={264}>
        <M2Label>Model</M2Label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '0 8px' }}>
          {models.map((x) => (
            <div key={x.id} style={{ display: 'flex', alignItems: 'stretch', gap: 4 }}>
              <button onClick={() => { setModel(x.id); setLibModel(null); setClip('Idle_Loop'); }} style={{
                display: 'flex', alignItems: 'center', gap: 9, padding: '8px 12px', borderRadius: 9, textAlign: 'left', flex: 1, minWidth: 0,
                border: '1px solid ' + (!libModel && model === x.id ? 'var(--ss-accent-border)' : 'transparent'),
                background: !libModel && model === x.id ? 'var(--ss-accent-soft)' : 'transparent',
                color: !libModel && model === x.id ? 'var(--ss-accent)' : 'var(--ss-text-2)', fontSize: 12.5, fontWeight: 500,
              }}>
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{x.id}</span>
                <span style={{ fontSize: 8.5, fontWeight: 600, letterSpacing: '0.12em', color: x.kind === 'RIGGED' ? 'var(--ss-success)' : 'var(--ss-text-3)', border: '1px solid var(--ss-border-soft)', borderRadius: 999, padding: '2px 7px', flexShrink: 0 }}>{x.kind}</span>
              </button>
              {x.kind === 'STATIC' && x.assetUrl && (
                <button
                  type="button"
                  title="Map this GLB's skeleton onto the shared 45-clip set"
                  onClick={() => setMappingTarget({ id: x.id, url: x.assetUrl! })}
                  style={{ flexShrink: 0, padding: '0 9px', fontSize: 9.5, fontWeight: 600, letterSpacing: '0.06em', borderRadius: 7, border: '1px solid var(--ss-border-soft)', background: 'transparent', color: 'var(--ss-text-3)' }}
                >
                  Rig…
                </button>
              )}
              {x.kind === 'STATIC' && x.assetUrl && (
                <button
                  type="button"
                  title="Place landmark markers to auto-rig this mesh onto the canonical skeleton"
                  onClick={() => setAutoRigTarget({ id: x.id, url: x.assetUrl! })}
                  style={{ flexShrink: 0, padding: '0 9px', fontSize: 9.5, fontWeight: 600, letterSpacing: '0.06em', borderRadius: 7, border: '1px solid var(--ss-border-soft)', background: 'transparent', color: 'var(--ss-text-3)' }}
                >
                  Auto-rig…
                </button>
              )}
            </div>
          ))}
        </div>
        {libraryModels.total > 0 && (
          <Fragment>
            <M2Label>Library · animated</M2Label>
            <div style={{ padding: '0 16px 8px' }}>
              <input className="ss-input" placeholder={'Search ' + libraryModels.total + ' models'} value={libSearch} onChange={(e) => setLibSearch(e.target.value)} style={{ padding: '7px 10px', fontSize: 12, borderRadius: 8 }} />
            </div>
            {[...libraryModels.byAuthor.entries()].map(([author, bundles]) => (
              <Fragment key={author}>
                <div style={{ padding: '10px 16px 3px', fontFamily: 'var(--ss-sans)', fontWeight: 600, fontSize: 9.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ss-text-3)' }}>{author}</div>
                {[...bundles.entries()].map(([bundle, models]) => (
                  <div key={bundle}>
                    <div style={{ padding: '4px 16px 2px', fontFamily: 'var(--ss-sans)', fontWeight: 500, fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ss-accent)', opacity: 0.7 }}>{bundle}</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 1, padding: '0 8px' }}>
                      {models.map((x) => (
                        <button key={x.assetId} onClick={() => { setLibModel(x); setClip(x.clips[0] ?? ''); setPlaying(true); }} style={{
                          display: 'flex', alignItems: 'center', gap: 8, padding: '5px 12px', borderRadius: 7, border: 'none', textAlign: 'left',
                          background: libModel?.assetId === x.assetId ? 'var(--ss-accent-soft)' : 'transparent',
                          color: libModel?.assetId === x.assetId ? 'var(--ss-accent)' : 'var(--ss-text-2)', fontSize: 12, fontWeight: 450,
                        }}>
                          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{x.name}</span>
                          <span style={{ fontSize: 9, color: 'var(--ss-text-3)', flexShrink: 0 }}>{x.clips.length}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </Fragment>
            ))}
            {libraryModels.matchTotal > libraryModels.shownCount && (
              <span style={{ fontSize: 10.5, color: 'var(--ss-text-3)', padding: '4px 16px 8px', display: 'block' }}>
                First {libraryModels.shownCount} of {libraryModels.matchTotal} - refine the search.
              </span>
            )}
          </Fragment>
        )}
        {libModel ? (
          <Fragment>
            <M2Label>Animations</M2Label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1, padding: '0 8px' }}>
              {libModel.clips.map((c) => (
                <button key={c} onClick={() => { setClip(c); setPlaying(true); }} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px', borderRadius: 7, border: 'none', textAlign: 'left',
                  background: clip === c ? 'var(--ss-accent-soft)' : 'transparent',
                  color: clip === c ? 'var(--ss-accent)' : 'var(--ss-text-2)', fontSize: 12, fontWeight: 450,
                }}>
                  {clip === c && playing ? <svg width="8" height="9" viewBox="0 0 10 11" fill="none" aria-hidden="true"><path d="M1.5 1.3v8.4l7.2-4.2-7.2-4.2z" fill="currentColor" /></svg> : <span style={{ width: 8 }} />}
                  {c.split('|').pop()}
                </button>
              ))}
            </div>
          </Fragment>
        ) : rigged ? (
          <Fragment>
            <M2Label>Animations</M2Label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '0 8px' }}>
              {ANIM_CLIP_GROUPS.map(([group, clips]) => (
                <div key={group}>
                  <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ss-text-3)', padding: '0 8px 5px' }}>{group}</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {clips.map((c) => (
                      <button key={c} onClick={() => { setClip(c); setPlaying(true); }} style={{
                        display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px', borderRadius: 7, border: 'none', textAlign: 'left',
                        background: clip === c ? 'var(--ss-accent-soft)' : 'transparent',
                        color: clip === c ? 'var(--ss-accent)' : 'var(--ss-text-2)', fontSize: 12, fontWeight: 450,
                      }}>
                        {clip === c && playing ? <svg width="8" height="9" viewBox="0 0 10 11" fill="none" aria-hidden="true"><path d="M1.5 1.3v8.4l7.2-4.2-7.2-4.2z" fill="currentColor" /></svg> : <span style={{ width: 8 }} />}
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Fragment>
        ) : (
          <div style={{ padding: '16px 16px 0', fontSize: 12, color: 'var(--ss-text-3)', lineHeight: 1.6 }}>
            {m.kind === 'PROP' ? 'Props have no clips of their own. Attach to a rigged model and the hand animation carries it.' : 'No rig on this asset yet. Custom rigging for created and imported models is on the roadmap.'}
          </div>
        )}
      </M2Panel>

      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 0, background: 'radial-gradient(ellipse 70% 60% at 50% 40%, var(--ss-surface), var(--ss-bg-2) 80%)' }}>
          {libModel ? (
            <LibraryGalleryStage url={libModel.url} clip={clip} speed={speedPct / 50} paused={!playing} background="var(--ss-bg-2)" />
          ) : isRetargeted ? (
            <RetargetedGalleryStage
              url={m.custom!.importedGlbUrl!}
              mapping={m.custom!.boneMapping!}
              clip={galleryClip}
              speed={speedPct / 50}
              paused={!playing}
              background="var(--ss-bg-2)"
            />
          ) : rigged ? (
            <AnimationGalleryStage
              key={model}
              clip={galleryClip}
              speed={speedPct / 50}
              paused={!playing}
              background="var(--ss-bg-2)"
              prop={galleryProp}
              loader={BUILT_IN_RIG_LOADERS[model]}
            />
          ) : m.assetUrl ? (
            <LibraryGalleryStage url={m.assetUrl} background="var(--ss-bg-2)" />
          ) : (
            <ImageSlot shape="rect" placeholder="Model render"
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block' }} />
          )}
          <ViewportChip style={{ top: 14, left: 14 }}>
            {libModel
              ? libModel.name + ' · ' + (clip.split('|').pop() ?? '').toLowerCase()
              : model + (rigged ? ' · ' + clip.toLowerCase() : '') + (selectedAttachment ? ' · ' + selectedAttachment.id : '')}
          </ViewportChip>
          <ViewportChip style={{ bottom: 14, left: '50%', transform: 'translateX(-50%)' }}>Drag to orbit · Scroll zooms</ViewportChip>
        </div>
        {/* playback + timeline */}
        <div style={{ flexShrink: 0, borderTop: '1px solid var(--ss-border-soft)', background: 'var(--ss-bg-2)', padding: '12px 16px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button className="ss-btn ss-btn--primary" onClick={() => setPlaying(!playing)} style={{ width: 30, height: 30, padding: 0, borderRadius: '50%', justifyContent: 'center' }} aria-label={playing ? 'Pause' : 'Play'}>
                {playing ? <svg width="9" height="10" viewBox="0 0 10 11" fill="none" aria-hidden="true"><path d="M2 1.5v8M8 1.5v8" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" /></svg> : <svg width="9" height="10" viewBox="0 0 10 11" fill="none" aria-hidden="true"><path d="M1.5 1.3v8.4l7.2-4.2-7.2-4.2z" fill="currentColor" /></svg>}
              </button>
              <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--ss-text-3)' }}>{libModel ? (clip.split('|').pop() ?? clip) + ' · loop' : rigged ? clip + ' · loop' : 'Static preview'}</span>
            </div>
            <button className="ss-btn ss-btn--ghost" disabled title="Authoring clips from saved poses is on the roadmap" style={{ padding: '4px 11px', fontSize: 11, borderRadius: 7, opacity: 0.45 }}>+ Clip from poses · soon</button>
          </div>
          <div style={{ position: 'relative', height: 30, border: '1px solid var(--ss-border-soft)', borderRadius: 9, background: 'rgba(0,0,0,0.3)', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontSize: 9.5, fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ss-text-3)' }}>Multi-track sequencing lives in the Cutscenes mode</span>
          </div>
        </div>
      </div>

      <M2Panel side="right" width={264}>
        <M2Label>Playback</M2Label>
        <EdSlider label="Speed" suffix="%" value={speedPct} onChange={setSpeedPct} />
        <M2Label>Attachment</M2Label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '0 16px', marginBottom: 6 }}>
          <select
            className="ss-input"
            value={weapon}
            onChange={(e) => setWeapon(e.target.value)}
            disabled={!canAttachProp}
            title={canAttachProp ? undefined : 'Prop attachment is available on the stock rigged model.'}
            style={{ appearance: 'none', padding: '8px 11px', fontSize: 12.5, borderRadius: 9, opacity: canAttachProp ? 1 : 0.45 }}
          >
            <option value="None">None</option>
            {attachableAssets.map((a) => (
              <option key={a.id} value={a.id}>{a.id}</option>
            ))}
          </select>
          <span style={{ fontSize: 11, color: 'var(--ss-text-3)', lineHeight: 1.5 }}>
            {canAttachProp
              ? attachableAssets.length > 0
                ? 'Attaches to the right hand and rides it through every clip.'
                : 'Upload a GLB asset to attach it here as a prop.'
              : 'Prop attachment is available on the stock rigged model.'}
          </span>
        </div>
        <M2Label>Posing</M2Label>
        <div style={{ padding: '0 16px', fontSize: 11.5, color: 'var(--ss-text-3)', lineHeight: 1.55 }}>
          Direct bone posing and a pose library arrive with custom rigging.
        </div>
        <M2Label>Rigging</M2Label>
        <div style={{ margin: '0 16px', padding: '12px 14px', border: '1px solid var(--ss-border-soft)', borderRadius: 10, fontSize: 11.5, color: 'var(--ss-text-3)', lineHeight: 1.55 }}>
          {customCharacters.length > 0
            ? `Pre-rigged models pose and play today. This project also has ${customCharacters.length} custom rig(s) registered - select one on the left to pose it.`
            : 'Pre-rigged models pose and play today. Rigging your own created or imported models is on the roadmap.'}
        </div>
      </M2Panel>
    </div>
  );
}

/* ── Preview (playtest) ───────────────────────────────────────────────── */

export interface HudOverlayViewProps {
  hud: HudDoc;
  gameData: GameDataDoc;
  live?: HudLiveState;
}

/**
 * Pure HUD render: the authored widget set positioned and tuned, with live
 * values where a real source exists. Prop-driven (no doc-store hook) so both
 * the editor Preview and the public play surface can use it - Preview
 * fetches the docs via the authenticated editor store (`HudOverlay` below),
 * the play surface via the public `/play/{slug}/docs/{kind}` endpoint
 * (`screens-play.tsx`), and this component doesn't care which.
 */
export function HudOverlayView({ hud, gameData, live }: HudOverlayViewProps) {
  const hotbarItems = (gameData.items ?? []).map((i) => i.name);
  const firstDialogue = gameData.dialogue?.[0];
  const dialogueLine = firstDialogue ? { speaker: firstDialogue.speaker, text: firstDialogue.text } : undefined;
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 5 }}>
      {HUD_WIDGETS.filter((w) => hud.active.includes(w.id)).map((w) => {
        const t = hudTuning(hud, w.id);
        return (
          <div
            key={w.id}
            style={{
              position: 'absolute',
              ...w.pos,
              width: w.w,
              height: w.h,
              transform: `${(w.pos.transform as string | undefined) ?? ''} scale(${t.scale / 100})`.trim(),
              opacity: t.opacity / 100,
              background: 'rgba(7,7,9,0.5)',
              backdropFilter: 'blur(4px)',
              border: '1px solid rgba(255,255,255,0.07)',
              borderRadius: 8,
              padding: '4px 8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <HudWidgetMock id={w.id} items={hotbarItems} live={live} dialogueLine={dialogueLine} />
          </div>
        );
      })}
      {live?.outcome && (
        // The win/lose result panel: shows the moment LogicEvaluator.outcome()
        // resolves (GameRuntime's own onTick/onFrameResolved/onPlayerStateChange
        // check it every frame - see checkMatchOutcome there), same team/win/lose
        // phrasing as the feed line above (formatMatchOutcome). GameRuntime
        // itself gates further movement/action input the same frame this
        // becomes true - this panel is the visible half of that, not what
        // enforces it.
        <div
          style={{
            position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(4,4,5,0.72)', backdropFilter: 'blur(6px)', zIndex: 10,
          }}
        >
          <div style={{
            textAlign: 'center', padding: '26px 46px', borderRadius: 16,
            background: 'rgba(10,10,13,0.82)', border: '1px solid var(--ss-border-soft)',
          }}>
            <span style={{
              fontFamily: 'var(--ss-sans)', fontWeight: 500, fontSize: 11, letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: live.outcome.result === 'win' ? 'var(--ss-success, #8fd19e)' : 'var(--ss-danger, #e06060)',
            }}>
              Match over
            </span>
            <h1 className="ss-display" style={{ fontSize: 'clamp(2rem, 6vw, 3.2rem)', margin: '8px 0 0', color: 'var(--ss-text)' }}>
              {formatMatchOutcome(live.outcome)}
            </h1>
          </div>
        </div>
      )}
    </div>
  );
}

/** Fetches the HUD/Game-Data docs via the authenticated editor store, then
 * renders them with `HudOverlayView`. Editor Preview's call site. */
function HudOverlay({ live }: { live?: HudLiveState }) {
  const { data: hud } = useModeDocument<HudDoc>('hud', HUD_DOC_FALLBACK);
  const { data: gameData } = useModeDocument<GameDataDoc>('gamedata', GAMEDATA_DOC_FALLBACK);
  return <HudOverlayView hud={hud} gameData={gameData} live={live} />;
}

/**
 * Live HUD values for a running game: elapsed time, the player normalised
 * into the map bounds for the minimap, placed markers, and a feed of real
 * trigger enter/exit events derived from the player's movement through the
 * authored trigger volumes (the same containment diffing the realtime
 * driver uses). Returns the live state plus the per-frame position
 * callback. Prop-driven (triggers/markers passed in, not read from the
 * editor's map-editor store) so both editor Preview and the public play
 * surface can use it - Preview passes the live-editing store's arrays,
 * the play surface passes the published map document's own.
 */
export function useHudPlayState(
  map: MapDocument,
  triggers: MapTriggerVolume[],
  markers: MapRoomMarker[],
  /** Whether the project is actually running in 2D this mount - decides
   * which coordinate system `bounds` (and so the minimap) normalises
   * against. Not inferred from `map.tilemap2d`'s mere presence: a document
   * can carry both a 2D layer and 3D floors (e.g. after switching
   * projection), and only one is ever the live coordinate system a given
   * mount's `onPosition` calls actually report in. */
  is2D: boolean,
  /** The running `ClientLogicRuntime`, if any (GameRuntime hands this back
   * via `logicRuntimeRef` once play mode mounts it) - sampled on the same
   * poll as everything else here so `health`/`ammo` land in `live` without
   * a second timer. */
  logicRuntimeRef?: MutableRefObject<ClientLogicRuntimeHandle | null>,
): { live: HudLiveState; onPosition: (x: number, y: number, z: number) => void; onLogicEffect: (effect: LogicEffect) => void } {
  const bounds = useMemo(() => {
    // 2D reports player/marker positions in tile coordinates (x in
    // [0, width), the position callback's z in [0, height) - see
    // `GameRuntime`'s 2D branch) - the map's own `floors`/`panels` are a
    // 3D-only concept and stay empty for a 2D project, which is what made
    // this always fall through to the `-10..10` fallback below and
    // miscalibrate the minimap for every 2D game.
    if (is2D && map.tilemap2d) {
      return { minX: 0, maxX: map.tilemap2d.width, minZ: 0, maxZ: map.tilemap2d.height };
    }
    let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
    for (const floor of map.floors) {
      for (const panel of floor.panels) {
        minX = Math.min(minX, panel.center[0] - panel.size[0] / 2);
        maxX = Math.max(maxX, panel.center[0] + panel.size[0] / 2);
        minZ = Math.min(minZ, panel.center[1] - panel.size[1] / 2);
        maxZ = Math.max(maxZ, panel.center[1] + panel.size[1] / 2);
      }
    }
    if (!Number.isFinite(minX)) return { minX: -10, maxX: 10, minZ: -10, maxZ: 10 };
    return { minX, maxX, minZ, maxZ };
  }, [map.floors, map.tilemap2d, is2D]);

  const normalise = useCallback(
    (x: number, z: number): [number, number] => [
      Math.min(1, Math.max(0, (x - bounds.minX) / (bounds.maxX - bounds.minX || 1))),
      Math.min(1, Math.max(0, (z - bounds.minZ) / (bounds.maxZ - bounds.minZ || 1))),
    ],
    [bounds],
  );

  const positionRef = useRef<[number, number, number] | null>(null);
  const insideRef = useRef(new Set<string>());
  const feedRef = useRef<string[]>([]);
  const triggersRef = useRef(triggers);
  useEffect(() => {
    triggersRef.current = triggers;
  }, [triggers]);

  const onPosition = useCallback((x: number, _y: number, z: number) => {
    positionRef.current = [x, _y, z];
    for (const volume of triggersRef.current) {
      const inside =
        Math.abs(x - volume.center[0]) <= volume.size[0] / 2 &&
        Math.abs(z - volume.center[1]) <= volume.size[1] / 2;
      const label = String(volume.params?.site ?? volume.eventName ?? volume.id);
      const was = insideRef.current.has(volume.id);
      if (inside && !was) {
        insideRef.current.add(volume.id);
        feedRef.current.push(`Entered ${label}`);
      } else if (!inside && was) {
        insideRef.current.delete(volume.id);
        feedRef.current.push(`Left ${label}`);
      }
    }
  }, []);

  // Authored Logic `announce`/`emit`/`item` effects, formatted into the same
  // feed `onPosition`'s own trigger-enter/exit lines already push into -
  // this is `GameRuntime`'s `onLogicEffect` prop's actual call site (it had
  // none before this, anywhere in the app, so a fired rule's effect was
  // computed and then discarded). Covers both dimensions: GameRuntime
  // produces `LogicEffect`s identically for 2D and 3D, only how the
  // triggering event was derived differs.
  const onLogicEffect = useCallback((effect: LogicEffect) => {
    if (effect.kind === 'announce') feedRef.current.push(effect.message);
    else if (effect.kind === 'emit') feedRef.current.push(`Event: ${effect.event}`);
    else if (effect.kind === 'item') feedRef.current.push(`${effect.delta >= 0 ? '+' : ''}${effect.delta} ${effect.item}`);
    else if (effect.kind === 'win' || effect.kind === 'lose') feedRef.current.push(formatMatchOutcome({ result: effect.kind, team: effect.team }));
    else feedRef.current.push(`${effect.title ?? effect.questId}: ${effect.status}`);
  }, []);

  const [live, setLive] = useState<HudLiveState>({ elapsedMs: 0, player: null, markers: [], feed: [] });
  useEffect(() => {
    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      const position = positionRef.current;
      const runtime = logicRuntimeRef?.current;
      setLive({
        elapsedMs: Date.now() - startedAt,
        player: position ? normalise(position[0], position[2]) : null,
        markers: markers.map((m) => normalise(m.position[0], m.position[1])),
        feed: feedRef.current.slice(-4),
        health: runtime ? runtime.variable('health') : undefined,
        ammo: runtime ? runtime.variable('ammo') : undefined,
        // Sampled on the same poll as health/ammo, not checked per-frame
        // here - GameRuntime itself already reacts to the same
        // `outcome()` the instant it resolves (input-gating cannot wait on
        // a 250ms poll); this is only the HUD's own display of it.
        outcome: runtime ? runtime.outcome() : null,
      });
    }, 250);
    return () => window.clearInterval(timer);
  }, [normalise, markers, logicRuntimeRef]);

  return { live, onPosition, onLogicEffect };
}

/** Fetches the audio doc via the authenticated editor store, then plays it
 * with `useAudioPlayback`. Editor Preview's call site. */
function usePreviewAudio(
  assets: StudioAssetRecord[],
  onEngineReady?: (engine: AudioEngine) => void,
  onEngineDispose?: () => void,
): (x: number, y: number, z: number) => void {
  const { data: storedAudio } = useModeDocument<AudioDoc>('audio', AUDIO_DOC_FALLBACK);
  const doc = useMemo(() => migrateAudioDoc(storedAudio), [storedAudio]);
  return useAudioPlayback(assets, doc, onEngineReady, onEngineDispose);
}

/**
 * Positional audio for a running game: an AudioEngine playing the audio
 * doc's zones as looping voices, attenuated live by player distance.
 * Returns the per-frame player-position callback for GameRuntime.
 * Prop-driven (the doc is passed in, not read from the editor's doc store)
 * so both editor Preview and the public play surface can use it.
 * `onEngineReady`/`onEngineDispose` let a caller (the scripting console)
 * reach the same `AudioEngine` instance this hook owns, without this hook
 * needing to know anything about scripting - it only ever reports its own
 * lifecycle.
 */
export function useAudioPlayback(
  assets: StudioAssetRecord[],
  doc: AudioDoc,
  onEngineReady?: (engine: AudioEngine) => void,
  onEngineDispose?: () => void,
): (x: number, y: number, z: number) => void {
  const engineRef = useRef<AudioEngine | null>(null);

  useEffect(() => {
    const engine = new AudioEngine();
    engineRef.current = engine;
    onEngineReady?.(engine);
    // Browsers gate audio behind a user gesture; the first interaction with
    // the running game (click to capture, WASD) starts or resumes the context.
    const unlock = () => engine.ensureStarted();
    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
      engine.dispose();
      engineRef.current = null;
      onEngineDispose?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one AudioEngine per mount, same as every other per-session piece of state in this file; onEngineReady/onEngineDispose are read at mount time only, deliberately not re-triggering this effect if the caller passes a new inline function each render.
  }, []);

  const specs = useMemo<AudioZoneVoiceSpec[]>(
    () => doc.zones.flatMap((zone) => {
      const asset = assets.find((a) => a.id === zone.sound && a.kind === 'audio');
      if (!asset) return [];
      return [{
        id: zone.id,
        url: asset.downloadUrl,
        x: zone.x,
        z: zone.z,
        radius: zone.radius,
        falloff: zone.falloff,
        category: 'ambience' as const,
      }];
    }),
    [doc.zones, assets],
  );

  useEffect(() => {
    void engineRef.current?.setZones(specs);
  }, [specs]);

  useEffect(() => {
    engineRef.current?.setMix({
      masterVolume: doc.mixer.master / 100,
      musicVolume: doc.mixer.music / 100,
      sfxVolume: doc.mixer.sfx / 100,
      ambienceVolume: doc.mixer.ambience / 100,
    });
  }, [doc.mixer]);

  return useCallback((x: number, _y: number, z: number) => {
    engineRef.current?.updateListener(x, z);
  }, []);
}

export function PreviewRoute() {
  const { project, projectId, editorBase } = useEditorHost();
  const instances = useMapEditorStore((state) => state.objectInstances);
  const triggers = useMapEditorStore((state) => state.triggers);
  const markers = useMapEditorStore((state) => state.markers);
  const document = useMemo<MapDocument>(
    () => ({ ...project.map, objectInstances: instances }),
    [project.map, instances],
  );
  const is2dProjection = project.config.projection === '2D';
  const audioEngineRef = useRef<AudioEngine | null>(null);
  const onAudioEngineReady = useCallback((engine: AudioEngine) => { audioEngineRef.current = engine; }, []);
  const onAudioEngineDispose = useCallback(() => { audioEngineRef.current = null; }, []);
  const onAudioPosition = usePreviewAudio(project.assets, onAudioEngineReady, onAudioEngineDispose);
  const { data: logicDoc } = useModeDocument<LogicDoc>('logic', LOGIC_DOC_FALLBACK);
  // Game Setup's gravity field, previously saved and never read by any
  // runtime - fed into GameRuntime's platformer branch below.
  const { data: gameSetupDoc, loading: gameSetupLoading } = useModeDocument<GameSetupDoc>('gamesetup', GAMESETUP_DOC_FALLBACK);
  const platformerConfig = useMemo<Partial<PlatformerConfig> | undefined>(() => {
    const gravity = Number(gameSetupDoc.gravity);
    return Number.isFinite(gravity) ? { gravity } : undefined;
  }, [gameSetupDoc.gravity]);
  const logicRuntimeRef = useRef<ClientLogicRuntimeHandle | null>(null);
  const scriptLogicRef = useRef<ClientLogicRuntime | null>(null);
  // The HollowScript scripting console's runtime for this play session -
  // constructed here (not in DockConsole) since only this component holds
  // the live handles (liveSceneStore, the concrete ClientLogicRuntime,
  // AudioEngine) a script actually runs against; registered into
  // scriptConsoleStore so DockConsole (rendered in the shared editor chrome,
  // outside this route) can reach it, and kept in sync with Game Setup's own
  // "Scripting capabilities" panel (task item 4's default-deny-per-project
  // grant). Extracted into `usePreviewScriptRuntime` (see that module's own
  // doc comment) so this security-relevant wiring has automated coverage
  // (`tests/unit/usePreviewScriptRuntime.test.ts`) instead of only being
  // manually verified live in the browser.
  usePreviewScriptRuntime({ scriptLogicRef, audioEngineRef }, projectId, gameSetupDoc.scriptCapabilities, !gameSetupLoading);
  const { live, onPosition: onHudPosition, onLogicEffect } = useHudPlayState(document, triggers, markers, is2dProjection, logicRuntimeRef);
  const onPlayerPosition = useCallback(
    (x: number, y: number, z: number) => {
      onAudioPosition(x, y, z);
      onHudPosition(x, y, z);
    },
    [onAudioPosition, onHudPosition],
  );
  // What actually mounted, not what was requested: a '2D' request with no
  // saved tilemap renders neither scene, and the control hint below must
  // match that, not the project's projection setting.
  const [activeProjection, setActiveProjection] = useState<'2D' | '3D' | null>(null);
  const [lockError, setLockError] = useState(false);
  const playerCallbacks = useMemo(() => ({
    onLockError: () => setLockError(true),
    onLockChange: (locked: boolean) => { if (locked) setLockError(false); },
  }), []);
  // Escape already has a job while the mouse is captured (native pointer
  // unlock, surfaced by the "ESC to release" hint below); once it isn't
  // captured, the same key exits preview entirely - "Exit preview" is
  // otherwise a mouse-only ghost button with no visible keyboard
  // alternative, unlike that hint right next to it.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || window.document.pointerLockElement) return;
      navigate(`${editorBase}/edit`);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [editorBase]);
  // A brand-new project still mounts a full lit scene (3D) or a tilemap
  // canvas (2D) with nothing in it - the player just stands in a void with
  // no indication that's expected, rather than a broken preview.
  const emptyScene = is2dProjection
    ? Boolean(document.tilemap2d) && document.tilemap2d!.cells.every((c) => c === null) && (document.tilemap2d!.entities ?? []).length === 0
    : instances.length === 0 && document.floors.every((f) => f.walls.length === 0 && f.panels.length === 0);
  return (
    <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
      <div style={{ position: 'relative', flex: 1, minWidth: 0, background: '#060607' }}>
        <GameRuntime
          mapDocument={document}
          config={project.config}
          characters={project.characters}
          poseLibraries={project.poseLibraries}
          mode="play"
          projection={is2dProjection ? '2D' : '3D'}
          onPlayerPosition={onPlayerPosition}
          onActiveProjection={setActiveProjection}
          callbacks={playerCallbacks}
          logicDocument={logicDoc}
          onLogicEffect={onLogicEffect}
          logicRuntimeRef={logicRuntimeRef}
          scriptLogicRef={scriptLogicRef}
          platformerConfig={platformerConfig}
          playerCharacterId={gameSetupDoc.playerCharacterId}
        />
        <HudOverlay live={live} />
        {emptyScene && (
          <div style={{
            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            textAlign: 'center', background: 'var(--ss-glass)', backdropFilter: 'blur(8px)',
            border: '1px solid var(--ss-border-soft)', borderRadius: 14, padding: '22px 28px', maxWidth: 320,
          }}>
            <div style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 6, color: 'var(--ss-text)' }}>Nothing built yet</div>
            <div style={{ fontSize: 12, color: 'var(--ss-text-3)', lineHeight: 1.55, marginBottom: 14 }}>
              This project has no {is2dProjection ? 'painted tiles or placed sprites' : 'placed objects'} yet.
            </div>
            <Link to={`${editorBase}/${is2dProjection ? 'pixel' : 'edit'}`} className="ss-btn ss-btn--primary ss-btn--sm">
              {is2dProjection ? 'Go to Pixel Studio' : 'Go to Edit'}
            </Link>
          </div>
        )}
        <ViewportChip style={{ top: 14, left: 14 }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--ss-success)' }} />
          PREVIEW · LOCAL
        </ViewportChip>
        <Link to={`${editorBase}/edit`} title="Esc" className="ss-btn ss-btn--ghost ss-btn--sm" style={{ position: 'absolute', top: 10, right: 14, background: 'var(--ss-glass)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', gap: 7 }}>
          Exit preview<span className="ss-kbd" style={{ fontSize: 9, padding: '0 5px' }}>Esc</span>
        </Link>
        {activeProjection && (
          <ViewportChip style={{ bottom: 14, left: '50%', transform: 'translateX(-50%)', color: lockError ? 'var(--ss-danger, #e06060)' : undefined }}>
            {activeProjection === '2D'
              ? 'WASD or arrow keys to move'
              : lockError
                ? 'Mouse capture was blocked by your browser - click the preview again to retry'
                : 'Click to capture mouse · ESC to release'}
          </ViewportChip>
        )}
        <PreviewConsoleOverlay />
      </div>
    </div>
  );
}

/**
 * Preview's own console overlay - the same backtick shortcut
 * (`toggleConsole`) that opens Edit mode's docked Console tab opens this
 * instead, since Preview is deliberately chrome-free (`showChrome` above)
 * and `BottomDock` never renders there. Floats over the running scene
 * rather than pushing it up, so a script can be hot-reloaded while the game
 * keeps rendering underneath - the actual point of tier 1
 * (docs/editor-consolidation/roadmap.md section 3.4's "does this feel real"
 * requirement).
 */
function PreviewConsoleOverlay() {
  const open = useScriptConsoleStore((state) => state.open);
  if (!open) return null;
  return (
    <div style={{
      position: 'absolute', bottom: 14, left: 14, right: 14, maxWidth: 640, height: 220,
      background: 'rgba(10,10,12,0.92)', backdropFilter: 'blur(10px)',
      border: '1px solid var(--ss-border-soft)', borderRadius: 12, overflow: 'hidden',
      display: 'flex', flexDirection: 'column', boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 14px', borderBottom: '1px solid var(--ss-border-soft)', flexShrink: 0 }}>
        <span style={{ fontFamily: 'var(--ss-sans)', fontWeight: 500, fontSize: 9.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ss-accent)' }}>Console</span>
        <button onClick={() => useScriptConsoleStore.getState().toggleOpen()} title="Close (`)" style={{ border: 'none', background: 'transparent', color: 'var(--ss-text-3)', fontSize: 10, padding: '4px 8px' }}>✕</button>
      </div>
      <DockConsole />
    </div>
  );
}

/* ── shell ────────────────────────────────────────────────────────────── */
const LEGACY_TABS: Record<string, string> = { play: 'preview', plan: 'edit' };

/**
 * The dock's Panel contract, applied to every existing mode's `*Route`
 * component. Each one registers as ONE full-size panel with its body
 * completely unchanged - see panel-system/types.ts's PanelDescriptor and
 * this branch's own scope note in the roadmap (docs/editor-consolidation/
 * roadmap.md, Phase 2's first task): decomposing a mode's own content into
 * multiple docked sub-panels is a later pass, not this one.
 *
 * The five presets (scene / art-assets / systems / setup / code) are
 * Blender-style named workspaces per the roadmap's UX decision
 * (docs/editor-consolidation/roadmap.md section 3.2), switched via the top
 * PresetSwitcher tab strip in panel-system/Workspace.tsx.
 *
 * Preview is deliberately NOT in this registry. It hides the preset-switcher/
 * dock/status-bar entirely (`showChrome` below) and is reached only via the
 * TopBar's own Preview link - a different, chrome-free surface, not a
 * workspace tab. ScreenEditor keeps rendering it directly, exactly as before
 * this branch.
 */
// 'code' is appended, not inserted earlier - Workspace.tsx's defaultPresetId
// and resetAllWorkspaces both key off presets[0], which must stay 'scene'.
// Exported for tests only (see tests/unit/panelRegistry.test.ts) - guards
// exactly this ordering requirement.
export const WORKSPACE_PRESETS: PresetDescriptor[] = [
  { id: 'scene', label: 'Scene' },
  { id: 'art', label: 'Art & Assets' },
  { id: 'systems', label: 'Systems' },
  { id: 'setup', label: 'Setup' },
  { id: 'code', label: 'Code' },
];

/** Modes gated to one projection. Used both to build the registry's
 * `dimension` field and to redirect a legacy URL for a mode invalid in the
 * project's current projection - the same guard the old per-mode dispatch
 * below used to apply inline. */
const DIMENSION_GATED_MODES: Partial<Record<string, '2D' | '3D'>> = {
  model: '3D', characters: '3D', animation: '3D', pixel: '2D', fx: '3D', cutscene: '3D', gunsmith: '3D',
};

/** A legacy `/app/editor/:id/:mode` URL for a mode that has since been split
 * into sibling panels (docs/editor-consolidation/panel-content-spec.md) no
 * longer names a single panel id directly - it resolves to that mode-group's
 * primary panel instead ("open the workspace with preset P, focused on
 * panel X", per roadmap.md's Phase 2 requirement). A mode not listed here
 * still has exactly one panel, so its own id is already the right target. */
const LEGACY_MODE_PRIMARY_PANEL: Partial<Record<string, string>> = {
  logic: 'systems.logic',
  hud: 'systems.hud',
  audio: 'systems.audio',
  data: 'systems.data',
  fx: 'systems.fx',
  cutscene: 'systems.cutscene',
  intro: 'systems.intro',
  game: 'game.rules',
  // 'environment' is now one panel again, so a legacy /environment URL
  // names it directly - kept in this table anyway (rather than relying on
  // the identity fallback) because a bookmarked
  // /app/editor/:id/environment must keep resolving even if the panel is
  // ever renamed again.
  environment: 'environment',
};

function primaryPanelIdForMode(mode: string): string {
  return LEGACY_MODE_PRIMARY_PANEL[mode] ?? mode;
}

/** Thin wrapper registering the self-contained Gunsmith Studio screen (see
 * GunsmithStudioPage.tsx's own header comment) into the live editor's mode
 * rail. GunsmithStudioPage owns its own internal Workspace/panel set - this
 * just supplies the real project id, mirroring how MapRoute/CharactersRoute
 * read it from useEditorHost rather than accepting it as a prop. */
function GunsmithRoute() {
  const { projectId } = useEditorHost();
  return <GunsmithStudioPage projectId={projectId} />;
}

// Exported for tests only (see tests/unit/panelRegistry.test.ts) - this
// function builds plain PanelDescriptor data and calls no hooks itself, so
// it's safe to call outside a React render.
export function buildPanelRegistry(projection: '2D' | '3D', onAddAsset: () => void): PanelDescriptor[] {
  return [
    // Scene's three panels each own the full width and compose their own
    // columns internally (scenePanels.tsx / EditRoute / MapRoute), so this
    // preset declares no left or right dockview zone. Environment used to
    // register as three separate panels pinned to those zones, which left
    // its preset list and sliders flanking Edit and Map Builder as well -
    // see scenePanels.tsx for the measurements that motivated the change.
    { id: 'edit', title: 'Edit', presetId: 'scene', dimension: 'shared', canvasOwning: true, render: () => <EditRoute projection={projection} onAddAsset={onAddAsset} /> },
    { id: 'map', title: 'Map Builder', presetId: 'scene', dimension: 'shared', canvasOwning: true, render: () => <MapRoute projection={projection} /> },
    { id: 'environment', title: 'Environment', presetId: 'scene', dimension: 'shared', render: () => <SceneEnvironmentPanel projection={projection} /> },
    { id: 'model', title: 'Model Studio', presetId: 'art', dimension: DIMENSION_GATED_MODES.model!, canvasOwning: true, render: () => <ModelRoute /> },
    { id: 'characters', title: 'Characters', presetId: 'art', dimension: DIMENSION_GATED_MODES.characters!, canvasOwning: true, render: () => <CharactersRoute /> },
    { id: 'animation', title: 'Animation Studio', presetId: 'art', dimension: DIMENSION_GATED_MODES.animation!, canvasOwning: true, render: () => <AnimationRoute /> },
    { id: 'gunsmith', title: 'Gunsmith Kit', presetId: 'art', dimension: DIMENSION_GATED_MODES.gunsmith!, canvasOwning: true, render: () => <GunsmithRoute /> },
    { id: 'pixel', title: 'Pixel Studio', presetId: 'art', dimension: DIMENSION_GATED_MODES.pixel!, render: () => <PixelRoute /> },
    // Systems' seven subsystems are each one façade panel with no zone/group,
    // so they collapse into one flat tab strip (systemsPanels.tsx) - the
    // same UNGROUPED mechanism that makes Setup's four panels render as one
    // clean tab row instead of stacked dockview groups.
    { id: 'systems.logic', title: 'Logic', presetId: 'systems', dimension: 'shared', render: () => <SystemsLogicPanel /> },
    { id: 'systems.hud', title: 'HUD', presetId: 'systems', dimension: 'shared', render: () => <SystemsHudPanel /> },
    { id: 'systems.audio', title: 'Audio', presetId: 'systems', dimension: 'shared', render: () => <SystemsAudioPanel /> },
    { id: 'systems.fx', title: 'FX', presetId: 'systems', dimension: DIMENSION_GATED_MODES.fx!, canvasOwning: true, render: () => <SystemsFxPanel /> },
    { id: 'systems.data', title: 'Game Data', presetId: 'systems', dimension: 'shared', render: () => <SystemsDataPanel /> },
    { id: 'systems.cutscene', title: 'Cutscenes', presetId: 'systems', dimension: DIMENSION_GATED_MODES.cutscene!, canvasOwning: true, render: () => <SystemsCutscenePanel /> },
    { id: 'systems.intro', title: 'Intro Sequences', presetId: 'systems', dimension: 'shared', render: () => <SystemsIntroPanel /> },
    { id: 'game.rules', title: 'Setup: Rules', presetId: 'setup', dimension: 'shared', render: () => <GameRulesPanel /> },
    { id: 'game.camera', title: 'Setup: Camera', presetId: 'setup', dimension: 'shared', render: () => <GameCameraPanel /> },
    { id: 'game.world', title: 'Setup: World', presetId: 'setup', dimension: 'shared', render: () => <GameWorldPanel /> },
    { id: 'game.meta', title: 'Setup: Meta', presetId: 'setup', dimension: 'shared', render: () => <GameMetaPanel /> },
    // The Code workspace (docs/editor-consolidation follow-on): a real,
    // persisted, multi-file HollowScript editor with equal top-level billing
    // to Scene/Art/Systems/Setup (the Roblox Studio "Script tab" precedent),
    // not buried in BottomDock's Console tab the way the REPL-only surface
    // is - see codePanels.tsx.
    // Scripts (left), the editor (centre), and Output / Problems / Reference
    // sharing the right zone as one tab strip (no `group`, so they stay
    // siblings - the same UNGROUPED bucketing Setup's panels use).
    { id: 'code.files', title: 'Scripts', presetId: 'code', dimension: 'shared', zone: 'left', render: () => <CodeFileListPanel /> },
    { id: 'code.editor', title: 'Editor', presetId: 'code', dimension: 'shared', render: () => <CodeEditorPanel /> },
    { id: 'code.output', title: 'Output', presetId: 'code', dimension: 'shared', zone: 'right', render: () => <CodeOutputPanel /> },
    { id: 'code.problems', title: 'Problems', presetId: 'code', dimension: 'shared', zone: 'right', render: () => <CodeProblemsPanel /> },
    { id: 'code.reference', title: 'Reference', presetId: 'code', dimension: 'shared', zone: 'right', render: () => <CodeReferencePanel /> },
    // No cross-panel Inspector registration here on purpose (removed, not
    // forgotten): its 7 sections each duplicated a strictly more capable
    // in-context editor that already existed - Map Builder's own room panel
    // (screens-editor.tsx's MapRoute), Characters' own Identity panel
    // (editor-modes3.tsx), and Systems' facades (systemsPanels.tsx), which
    // since the flatten always show a subsystem's full list+editor together
    // rather than needing a separate summary surface.
  ];
}

/**
 * True below the editor's usability floor: the multi-panel chrome (mode
 * rail, bottom dock, drag handles, keyboard shortcuts) needs real screen
 * space and a mouse, and quietly squeezing it into a phone width produces
 * something worse than an honest "not here" - unreachable panels, gizmos
 * too small to grab, text too small to read. Re-evaluates on resize/rotate
 * (matchMedia, not a one-time check) so undocking a tablet or rotating it
 * updates live rather than needing a reload.
 */
function useIsBelowEditorFloor(breakpointPx = 860): boolean {
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < breakpointPx,
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpointPx - 1}px)`);
    const onChange = () => setNarrow(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [breakpointPx]);
  return narrow;
}

/** Shown instead of the editor chrome below the usability floor. Playing and
 * previewing a project's own runtime works everywhere (it's the same
 * `GameRuntime` the published game uses); building needs more room. */
function EditorBelowFloor({ editorBase }: { editorBase: string }) {
  return (
    <div
      className="ss-screen"
      style={{
        height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', textAlign: 'center', gap: 18, padding: '0 32px',
      }}
    >
      <Glyph size={40} />
      <h1 className="ss-display" style={{ fontSize: 'clamp(1.6rem, 7vw, 2rem)' }}>Open this on a bigger screen</h1>
      <p style={{ color: 'var(--ss-text-3)', fontSize: 14, maxWidth: 380, lineHeight: 1.6 }}>
        The editor&rsquo;s panels, drag handles and shortcuts need more room and a
        mouse. Playing and previewing your game works on any screen; building
        it needs a larger one.
      </p>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
        <Link to={`${editorBase}/preview`} className="ss-btn ss-btn--primary">Playtest instead</Link>
        <Link to="/app" className="ss-btn ss-btn--ghost">Back to projects</Link>
      </div>
    </div>
  );
}

export function ScreenEditor({ tab = 'edit', dim }: { tab?: string; dim?: string }) {
  // legacy route names map to the new modes
  const mode = LEGACY_TABS[tab] || tab;
  const { editorBase, projectId } = useEditorHost();
  const belowFloor = useIsBelowEditorFloor();
  useMapEditorToastBridge();
  const projection = dim === '2d' ? '2D' : dim === '3d' ? '3D'
    : (() => { try { return localStorage.getItem('ss-projection') === '2D' ? '2D' : '3D'; } catch { return '3D'; } })();
  useEffect(() => {
    if (dim) { try { localStorage.setItem('ss-projection', projection); } catch { /* ignore */ } }
  }, [dim, projection]);
  const [dockOpen, setDockOpen] = useState(mode === 'edit');
  const [dockTab, setDockTab] = useState('assets');
  // Distraction-free mode: hides the preset tab strip/Panels menu, the
  // bottom dock and the status bar so the docked panels fill the available
  // height - the "hide the surrounding chrome, keep the work" toggle Godot
  // (Ctrl+Shift+F11), Unity and Unreal all have some form of. The top bar
  // stays visible (Save/Publish/Preview are still reachable), and this is
  // the one non-shortcut way back out - hiding the toggle itself alongside
  // everything else it controls would leave no way to escape but memorising
  // the shortcut.
  const [distractionFree, setDistractionFree] = useState(false);
  const toggleDistractionFree = useCallback(() => setDistractionFree((v) => !v), []);
  const onAddAsset = useCallback(() => { setDockTab('assets'); setDockOpen(true); }, []);
  const panels = useMemo(() => buildPanelRegistry(projection, onAddAsset), [projection, onAddAsset]);
  // Same per-mode projection gate the old dispatch below used to apply
  // inline (e.g. `mode === 'characters' && (projection === '3D' ? ... :
  // <Navigate .../>)`) - a legacy URL for a mode invalid in this project's
  // current projection still redirects to Edit, same as before this branch.
  const modeInvalid = DIMENSION_GATED_MODES[mode] !== undefined && DIMENSION_GATED_MODES[mode] !== projection;
  const toggleConsole = useCallback(() => {
    if (mode === 'preview') {
      useScriptConsoleStore.getState().toggleOpen();
      return;
    }
    setDockOpen((open) => {
      setDockTab('console');
      return !(open && dockTab === 'console');
    });
  }, [dockTab, mode]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Shares the same "am I typing in a field" check as the rest of the
      // editor's shortcuts (useEditorShortcuts) - this one previously only
      // checked INPUT/TEXTAREA, missing SELECT and contenteditable targets.
      if (e.key === '`' && !inEditableTarget()) {
        e.preventDefault();
        toggleConsole();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.code === 'KeyF' && !inEditableTarget()) {
        e.preventDefault();
        toggleDistractionFree();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggleConsole, toggleDistractionFree]);
  const showChrome = mode !== 'preview';
  if (belowFloor && mode !== 'preview') {
    return <EditorBelowFloor editorBase={editorBase} />;
  }
  return (
    <div className="ss-screen ss-editor-workspace" style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* MapEditorOverlay (chrome='full') owns this in @sstk/build-kit's own
          editor, but Studio's Edit mode always renders chrome='viewport' (see
          useEditorShortcuts.ts / useMapEditorToastBridge.ts for the same
          re-homing), so without this every Library/Uploads/Core-pack tile in
          the Assets dock renders as a permanent placeholder. Skipped during
          Preview so it doesn't steal frames from playtesting. */}
      {showChrome && <ThumbnailRenderer />}
      <EditorTopBar mode={mode} projection={projection} panels={panels} distractionFree={distractionFree} onToggleDistractionFree={toggleDistractionFree} />
      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
          <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
            {/* Consolidates every viewport-owning panel below onto one shared
                WebGL context (docs/editor-consolidation/roadmap.md section
                3.3) via `@react-three/drei`'s `<View>` - Chrome caps live
                contexts at ~16/tab and silently kills the oldest past that,
                a risk the dockable panel workspace below makes directly
                reachable (several canvas-owning panels can now coexist).
                `ViewportHost` is a no-op wrapper (`display: contents`)
                around panels with no viewport of their own.
                `CharactersRoute`'s own preview stage is deliberately NOT
                wired into this - it keeps owning its own `<Canvas>`
                (separate, unrelated consolidation work). */}
            <ViewportHost>
              {modeInvalid ? (
                <Navigate to={editorBase + '/edit'} replace />
              ) : mode === 'preview' ? (
                // Chrome-free (showChrome above is already false for this
                // mode) and reached only via the TopBar's own Preview link -
                // kept outside the docked workspace entirely, unchanged from
                // before this branch.
                <PreviewRoute />
              ) : (
                // Keyed by project: a project switch (a different :id on the
                // same /app/editor/:id/:mode route, without an intervening
                // full navigation elsewhere) must not carry the previous
                // project's dockview instance - and thus its persisted-layout
                // load, scoped by projectId inside Workspace's own onReady -
                // across into the new one.
                <Workspace
                  key={projectId}
                  projectId={projectId}
                  projection={projection}
                  panels={panels}
                  presets={WORKSPACE_PRESETS}
                  activePanelId={primaryPanelIdForMode(mode)}
                  hideChrome={distractionFree}
                  onActivePanelChange={(panelId) => navigate(`${editorBase}/${panelId}`, { replace: true })}
                />
              )}
            </ViewportHost>
          </div>
          {showChrome && !distractionFree && <BottomDock open={dockOpen} tab={dockTab} setOpen={setDockOpen} setTab={setDockTab} />}
        </div>
      </div>
      {showChrome && !distractionFree && <EditorStatusBar onConsole={toggleConsole} mode={mode} />}
    </div>
  );
}
